import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import ActionModal from "../components/ActionModal";
import { formatLatexForDisplay } from "../lib/latexUtils";

const GOAL_LABELS = {
  ib_dp: "IB Board Exam (DP)",
  cambridge_igcse: "Cambridge (IGCSE / A-Levels)",
  cbse: "CBSE Board Exam",
  sat: "SAT Math Exam",
  jee: "JEE Main / Advanced",
  general: "General Practice & Homework"
};

export default function TutorAssignmentDetail({ apiBase, token }) {
  const { setId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [assignment, setAssignment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Stages: "submissions" | "rubric" | "grading" | "review" | "publish"
  const [currentStage, setCurrentStage] = useState("submissions");

  // Rubric Studio State
  const [rubrics, setRubrics] = useState([]);
  const [selectedRubricQIdx, setSelectedRubricQIdx] = useState(0);
  const [generatingRubrics, setGeneratingRubrics] = useState(false);
  const [savingRubrics, setSavingRubrics] = useState(false);
  const [rubricMessage, setRubricMessage] = useState(null);

  // Cohort Grading State
  const [gradingCohort, setGradingCohort] = useState(false);
  const [publishingGrades, setPublishingGrades] = useState(false);
  const [cohortMessage, setCohortMessage] = useState(null);

  // Action / Progress Modal State
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    progress: 0,
    progressLabel: "",
    detail: "",
    isLocked: false,
    confirmText: "OK",
    cancelText: "Cancel",
    onConfirm: null,
    onCancel: null
  });

  // Manual Marks Override Modal State
  const [overrideModal, setOverrideModal] = useState(null);
  const [savingOverride, setSavingOverride] = useState(false);
  const [overrideError, setOverrideError] = useState("");

  // Feedback Modal State (for standard homework remarks)
  const [feedbackAttemptId, setFeedbackAttemptId] = useState(null);
  const [feedbackStudentName, setFeedbackStudentName] = useState("");
  const [feedbackQuestionTitle, setFeedbackQuestionTitle] = useState("");
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [feedbackModalError, setFeedbackModalError] = useState("");
  const [feedbackModalSuccess, setFeedbackModalSuccess] = useState("");

  // Cohort Diagnostics and Inline Performance State
  const [cohortDiagnostics, setCohortDiagnostics] = useState(null);
  const [cohortDiagView, setCohortDiagView] = useState("overall"); // "overall" | "questions"
  const [selectedCohortQIdx, setSelectedCohortQIdx] = useState(0);

  // Detailed Feedback Modal State
  const [detailedFeedbackModal, setDetailedFeedbackModal] = useState(null);
  const [sendingDetailedFeedback, setSendingDetailedFeedback] = useState(false);
  const [detailedFeedbackSuccess, setDetailedFeedbackSuccess] = useState("");
  const [detailedFeedbackError, setDetailedFeedbackError] = useState("");

  // Batch Feedback State (for selected or all students)
  const [selectedStudentUids, setSelectedStudentUids] = useState(new Set());
  const [batchFeedbackModal, setBatchFeedbackModal] = useState(null);
  const [sendingBatchFeedback, setSendingBatchFeedback] = useState(false);
  const [batchFeedbackSuccess, setBatchFeedbackSuccess] = useState("");
  const [batchFeedbackError, setBatchFeedbackError] = useState("");

  // Evaluation states for homework attempts
  const [evaluatingAttemptIds, setEvaluatingAttemptIds] = useState(new Set());
  const [isBatchEvaluating, setIsBatchEvaluating] = useState(false);

  // Student Review Stage State & Roster Filters
  const [reviewStudentUid, setReviewStudentUid] = useState(null);
  const [reviewQuestionIdx, setReviewQuestionIdx] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [inlineRemarks, setInlineRemarks] = useState({});
  const [savingInlineRemarks, setSavingInlineRemarks] = useState({});
  const [inlineRemarkSaved, setInlineRemarkSaved] = useState({});

  const handleSaveInlineRemark = async (attemptId, studentName, questionTitle) => {
    if (!attemptId) return;
    const notes = inlineRemarks[attemptId] !== undefined ? inlineRemarks[attemptId] : "";
    setSavingInlineRemarks((prev) => ({ ...prev, [attemptId]: true }));
    try {
      const res = await fetch(`${apiBase}/api/tutor/attempts/${attemptId}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ notes })
      });
      if (!res.ok) throw new Error("Failed to save remark");
      await loadSubmissions(true);
      setInlineRemarkSaved((prev) => ({ ...prev, [attemptId]: true }));
      setTimeout(() => {
        setInlineRemarkSaved((prev) => ({ ...prev, [attemptId]: false }));
      }, 2500);
    } catch (err) {
      alert("Failed to save remark: " + err.message);
    } finally {
      setSavingInlineRemarks((prev) => ({ ...prev, [attemptId]: false }));
    }
  };

  const handleEvaluateSingleAttempt = async (attemptId) => {
    if (!attemptId || evaluatingAttemptIds.has(attemptId)) return;
    setEvaluatingAttemptIds((prev) => new Set([...prev, attemptId]));
    try {
      const res = await fetch(`${apiBase}/api/attempts/${attemptId}/analyze-equivalence`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.details?.message || "Failed to evaluate attempt");
      }
      await loadSubmissions(true);
    } catch (err) {
      console.error("[TutorAssignmentDetail] Single attempt eval error:", err);
      setActionModal({
        isOpen: true,
        type: "error",
        title: "Evaluation Failed",
        message: err.message || "Failed to evaluate attempt with Erwin AI.",
        confirmText: "Close",
        onConfirm: () => setActionModal((prev) => ({ ...prev, isOpen: false }))
      });
    } finally {
      setEvaluatingAttemptIds((prev) => {
        const next = new Set(prev);
        next.delete(attemptId);
        return next;
      });
    }
  };

  const handleBatchEvaluateHomework = async () => {
    if (isBatchEvaluating || !setId) return;
    setIsBatchEvaluating(true);
    try {
      const res = await fetch(`${apiBase}/api/tutor/assignments/${setId}/evaluate-homework`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to batch evaluate homework");
      }
      const data = await res.json();
      await loadSubmissions(true);
      setActionModal({
        isOpen: true,
        type: "info",
        title: "✓ Homework Evaluated",
        message: data.message || `Evaluated ${data.evaluatedCount || 0} homework submission(s).`,
        confirmText: "OK",
        onConfirm: () => setActionModal((prev) => ({ ...prev, isOpen: false }))
      });
    } catch (err) {
      console.error("[TutorAssignmentDetail] Batch eval error:", err);
      setActionModal({
        isOpen: true,
        type: "error",
        title: "Batch Evaluation Failed",
        message: err.message || "Failed to evaluate pending homework submissions.",
        confirmText: "Close",
        onConfirm: () => setActionModal((prev) => ({ ...prev, isOpen: false }))
      });
    } finally {
      setIsBatchEvaluating(false);
    }
  };

  const loadSubmissions = useCallback(async (silent = false) => {
    if (!token || !setId) return;
    if (!silent) setLoading(true);
    setError("");

    try {
      const res = await fetch(`${apiBase}/api/tutor/assignments/${setId}/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to load assignment submissions");
      }
      const data = await res.json();
      setAssignment(data.assignment);
      const loadedQuestions = data.questions || [];
      setQuestions(loadedQuestions);
      setSubmissions(data.submissions || []);
      setCohortDiagnostics(data.cohortDiagnostics || null);

      // Initialize or sync rubrics
      if (Array.isArray(data.assignment?.rubrics) && data.assignment.rubrics.length > 0) {
        setRubrics(data.assignment.rubrics);
      } else if (loadedQuestions.length > 0) {
        // Initialize empty rubrics for each question
        const initRubrics = loadedQuestions.map((q) => ({
          questionId: q._id,
          totalPoints: 100,
          goal: data.assignment?.targetGoal || "general",
          canonicalSolution: "",
          items: [
            { criterion: "Method & Concept Application", description: "Demonstrates correct approach and method", points: 40, deductionNotes: "-10 for conceptual gap" },
            { criterion: "Intermediate Step Accuracy", description: "Calculations, algebraic expansions, and transformations are correct", points: 40, deductionNotes: "-5 per arithmetic slip" },
            { criterion: "Final Result & Precision", description: "Correct final result with appropriate units or form", points: 20, deductionNotes: "-5 for missing units" }
          ],
          confirmed: false
        }));
        setRubrics(initRubrics);
      }
    } catch (err) {
      setError(err.message || "Failed to load assignment submissions");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiBase, token, setId]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  // ─────────────────────────────────────────────────────────────
  // RUBRIC STUDIO HANDLERS
  // ─────────────────────────────────────────────────────────────

  const handleGenerateAiRubric = async (singleQuestionId = null) => {
    if (!token || !setId) return;
    setGeneratingRubrics(true);
    setRubricMessage(null);

    try {
      const payload = { targetGoal: assignment?.targetGoal || "general" };
      if (singleQuestionId) {
        payload.questionId = singleQuestionId;
      }

      const res = await fetch(`${apiBase}/api/tutor/assignments/${setId}/rubrics/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to generate AI rubric");
      }

      const data = await res.json();
      if (singleQuestionId) {
        // Update only that question's rubric
        const updated = rubrics.map((r) => {
          const match = (data.rubrics || []).find((nr) => String(nr.questionId) === String(r.questionId));
          return match || r;
        });
        setRubrics(updated);
      } else {
        setRubrics(data.rubrics || []);
      }

      setRubricMessage({
        type: "success",
        text: `✨ AI generated 100-point rubric using ${GOAL_LABELS[assignment?.targetGoal] || "curriculum"} guidelines! Review points and confirm.`
      });
    } catch (err) {
      setRubricMessage({ type: "error", text: err.message || "Failed to generate AI rubric" });
    } finally {
      setGeneratingRubrics(false);
    }
  };

  const handleUpdateCriterion = (qIdx, itemIdx, field, value) => {
    const updated = [...rubrics];
    const qRubric = { ...updated[qIdx] };
    const items = [...(qRubric.items || [])];
    items[itemIdx] = { ...items[itemIdx], [field]: value };
    qRubric.items = items;
    updated[qIdx] = qRubric;
    setRubrics(updated);
  };

  const handleAddCriterion = (qIdx) => {
    const updated = [...rubrics];
    const qRubric = { ...updated[qIdx] };
    const items = [...(qRubric.items || [])];
    items.push({
      criterion: "Additional Evaluation Criterion",
      description: "Specify criterion requirements",
      points: 10,
      deductionNotes: ""
    });
    qRubric.items = items;
    updated[qIdx] = qRubric;
    setRubrics(updated);
  };

  const handleRemoveCriterion = (qIdx, itemIdx) => {
    const updated = [...rubrics];
    const qRubric = { ...updated[qIdx] };
    const items = (qRubric.items || []).filter((_, idx) => idx !== itemIdx);
    qRubric.items = items;
    updated[qIdx] = qRubric;
    setRubrics(updated);
  };

  const handleSaveRubrics = async () => {
    if (!token || !setId) return;

    // Validate that each rubric sums to exactly 100 points
    for (let i = 0; i < rubrics.length; i++) {
      const r = rubrics[i];
      const sum = (r.items || []).reduce((acc, it) => acc + (Number(it.points) || 0), 0);
      if (sum !== 100) {
        setRubricMessage({
          type: "error",
          text: `Problem ${i + 1} rubric total is ${sum} points. Every rubric must sum to exactly 100 points.`
        });
        return;
      }
    }

    setSavingRubrics(true);
    setRubricMessage(null);

    try {
      const res = await fetch(`${apiBase}/api/tutor/assignments/${setId}/rubrics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ rubrics })
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to save rubrics");
      }

      const data = await res.json();
      setRubrics(data.rubrics || []);
      setAssignment((prev) => ({ ...prev, gradingStatus: data.gradingStatus || "rubric_ready", rubrics: data.rubrics }));
      setRubricMessage({
        type: "success",
        text: "✓ 100-Point Rubrics confirmed and saved! You can now run AI Cohort Grading."
      });
    } catch (err) {
      setRubricMessage({ type: "error", text: err.message || "Failed to save rubrics" });
    } finally {
      setSavingRubrics(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // COHORT BATCH AI GRADING & PUBLISH HANDLERS
  // ─────────────────────────────────────────────────────────────

  const handleRunCohortGrading = async () => {
    if (!token || !setId) return;
    setGradingCohort(true);
    setCohortMessage(null);

    setActionModal({
      isOpen: true,
      type: "progress",
      title: "AI Cohort Grading in Progress",
      message: "Gemini is grading student submissions against your standardized 100-point rubrics. Screen is locked during processing.",
      progress: 0,
      progressLabel: "Starting evaluation engine...",
      detail: "Initializing cohort queue...",
      isLocked: true,
      confirmText: "OK",
      cancelText: "Cancel",
      onConfirm: null,
      onCancel: null
    });

    try {
      const res = await fetch(`${apiBase}/api/tutor/assignments/${setId}/grade-cohort`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to run cohort grading");
      }

      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("text/event-stream") && res.body) {
        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop(); // save remainder

          for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith("data:")) {
              const jsonStr = trimmed.replace(/^data:\s*/, "");
              if (!jsonStr) continue;
              try {
                const event = JSON.parse(jsonStr);
                if (event.type === "init") {
                  const total = event.total ?? event.totalAttempts ?? 0;
                  setActionModal(prev => ({
                    ...prev,
                    progress: 0,
                    progressLabel: `0 of ${total} items evaluated (0%)`,
                    detail: event.message || "Queue initialized"
                  }));
                } else if (event.type === "progress") {
                  const current = event.current ?? 0;
                  const total = event.total ?? event.totalAttempts ?? current;
                  setActionModal(prev => ({
                    ...prev,
                    progress: event.percent,
                    progressLabel: `${current} of ${total} items evaluated (${event.percent}%)`,
                    detail: `Evaluating: ${event.studentName} • ${event.questionTitle}`
                  }));
                } else if (event.type === "done") {
                  const graded = event.graded ?? event.gradedCount ?? 0;
                  const total = event.total ?? event.totalAttempts ?? graded;
                  setActionModal(prev => ({
                    ...prev,
                    type: "success",
                    title: "Cohort Grading Complete!",
                    message: event.message || `Successfully evaluated ${graded} submissions.`,
                    progress: 100,
                    progressLabel: `${graded} of ${total} evaluated (100%)`,
                    detail: "All solutions scored and feedback generated.",
                    isLocked: false,
                    confirmText: "View Results",
                    onConfirm: () => {
                      setActionModal(prev => ({ ...prev, isOpen: false }));
                    }
                  }));
                  await loadSubmissions(true);
                } else if (event.type === "error") {
                  throw new Error(event.error || "Grading error occurred");
                }
              } catch (parseErr) {
                if (parseErr.message && !parseErr.message.includes("JSON")) {
                  throw parseErr;
                }
              }
            }
          }
        }
      } else {
        const data = await res.json();
        setActionModal({
          isOpen: true,
          type: "success",
          title: "Cohort Grading Complete!",
          message: `✓ ${data.message || "AI Cohort Grading finished successfully!"}`,
          progress: 100,
          progressLabel: "100% Completed",
          detail: "All solutions evaluated.",
          isLocked: false,
          confirmText: "View Results",
          onConfirm: () => setActionModal(prev => ({ ...prev, isOpen: false }))
        });
        await loadSubmissions(true);
      }
    } catch (err) {
      setActionModal({
        isOpen: true,
        type: "error",
        title: "Grading Failed",
        message: err.message || "Failed to execute AI cohort grading",
        isLocked: false,
        confirmText: "Close",
        onConfirm: () => setActionModal(prev => ({ ...prev, isOpen: false }))
      });
      setCohortMessage({ type: "error", text: err.message || "Failed to execute AI cohort grading" });
    } finally {
      setGradingCohort(false);
    }
  };

  const handlePublishGrades = () => {
    if (!token || !setId) return;

    setActionModal({
      isOpen: true,
      type: "confirm",
      title: "Publish Cohort Grades?",
      message: "Are you sure you want to publish grades and rubric feedback? This will make scores and rubric step analyses immediately visible to all students.",
      confirmText: "Yes, Publish Grades",
      cancelText: "Cancel",
      isLocked: false,
      onCancel: () => setActionModal(prev => ({ ...prev, isOpen: false })),
      onConfirm: async () => {
        setPublishingGrades(true);
        setActionModal({
          isOpen: true,
          type: "progress",
          title: "Publishing Grades...",
          message: "Publishing evaluated marks and sending notifications to students...",
          progress: 50,
          progressLabel: "Publishing...",
          detail: "Updating student grade records...",
          isLocked: true
        });

        try {
          const res = await fetch(`${apiBase}/api/tutor/assignments/${setId}/publish-grades`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          });

          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || "Failed to publish grades");
          }

          const data = await res.json();
          setActionModal({
            isOpen: true,
            type: "success",
            title: "Grades Published!",
            message: data.message || "Grades and feedback have been published to students!",
            progress: 100,
            progressLabel: "100%",
            detail: "Students can now view their scores and rubric breakdown.",
            isLocked: false,
            confirmText: "OK",
            onConfirm: () => setActionModal(prev => ({ ...prev, isOpen: false }))
          });
          setCohortMessage({
            type: "success",
            text: `📢 ${data.message || "Grades and feedback have been published to students!"}`
          });
          await loadSubmissions(true);
        } catch (err) {
          setActionModal({
            isOpen: true,
            type: "error",
            title: "Publish Failed",
            message: err.message || "Failed to publish grades",
            isLocked: false,
            confirmText: "Close",
            onConfirm: () => setActionModal(prev => ({ ...prev, isOpen: false }))
          });
          setCohortMessage({ type: "error", text: err.message || "Failed to publish grades" });
        } finally {
          setPublishingGrades(false);
        }
      }
    });
  };

  // ─────────────────────────────────────────────────────────────
  // MARKS OVERRIDE HANDLERS
  // ─────────────────────────────────────────────────────────────

  const handleOpenOverride = (attemptId, studentName, questionTitle, currentMarks, marksMax, teacherNotes) => {
    setOverrideModal({
      attemptId,
      studentName,
      questionTitle,
      marksMax: marksMax || 10,
      marksAwarded: currentMarks !== null && currentMarks !== undefined ? currentMarks : marksMax || 10,
      teacherNotes: teacherNotes || ""
    });
    setOverrideError("");
  };

  const handleSaveOverride = async (e) => {
    e.preventDefault();
    if (!overrideModal) return;

    setSavingOverride(true);
    setOverrideError("");

    try {
      const res = await fetch(`${apiBase}/api/tutor/assignments/${setId}/override-marks`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          attemptId: overrideModal.attemptId,
          marksAwarded: Number(overrideModal.marksAwarded),
          teacherNotes: overrideModal.teacherNotes
        })
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to override marks");
      }

      setOverrideModal(null);
      await loadSubmissions(true);
    } catch (err) {
      setOverrideError(err.message || "Failed to save marks override");
    } finally {
      setSavingOverride(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // STANDARD HOMEWORK FEEDBACK HANDLERS
  // ─────────────────────────────────────────────────────────────

  const handleOpenFeedback = (attemptId, studentName, questionTitle, existingNotes) => {
    setFeedbackAttemptId(attemptId);
    setFeedbackStudentName(studentName);
    setFeedbackQuestionTitle(questionTitle);
    setFeedbackNotes(existingNotes || "");
    setFeedbackModalError("");
    setFeedbackModalSuccess("");
  };

  const handleSaveFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackAttemptId) return;

    setSavingFeedback(true);
    setFeedbackModalError("");
    setFeedbackModalSuccess("");

    try {
      const res = await fetch(`${apiBase}/api/tutor/attempts/${feedbackAttemptId}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ notes: feedbackNotes })
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to save feedback");
      }

      setFeedbackModalSuccess("Teacher's remark saved successfully! ✓");
      setFeedbackAttemptId(null);
      setFeedbackModalSuccess("");
      await loadSubmissions();
      setActionModal({
        isOpen: true,
        type: "success",
        title: "Problem Remark Saved! ✓",
        message: "Your problem feedback has been saved and the student notified in their My Tutor action center.",
        confirmText: "Done",
        onConfirm: () => setActionModal((prev) => ({ ...prev, isOpen: false }))
      });
    } catch (err) {
      setFeedbackModalError(err.message || "Failed to save feedback");
    } finally {
      setSavingFeedback(false);
    }
  };

  const scrollToAndExpandStudent = (studentUid) => {
    setReviewStudentUid(studentUid);
    setReviewQuestionIdx(0);
    setCurrentStage("review");
  };

  const handleOpenDetailedFeedback = (sub) => {
    const diag = sub.performanceDiagnostics;
    setDetailedFeedbackModal({
      studentUid: sub.studentUid,
      studentName: sub.name,
      title: assignment?.title || "Assignment",
      strengths: diag?.strengths || [],
      algebraicNotes: diag?.algebraicErrors || [],
      focusPoints: diag?.focusPoints || [],
      personalNote: ""
    });
    setDetailedFeedbackError("");
    setDetailedFeedbackSuccess("");
  };

  const handleSendDetailedFeedback = async (e) => {
    e.preventDefault();
    if (!detailedFeedbackModal) return;
    setSendingDetailedFeedback(true);
    setDetailedFeedbackError("");
    setDetailedFeedbackSuccess("");

    try {
      const res = await fetch(`${apiBase}/api/tutor/assignments/${setId}/send-student-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          studentUid: detailedFeedbackModal.studentUid,
          personalNote: detailedFeedbackModal.personalNote,
          strengths: detailedFeedbackModal.strengths,
          algebraicNotes: detailedFeedbackModal.algebraicNotes,
          focusPoints: detailedFeedbackModal.focusPoints
        })
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || "Failed to dispatch feedback");
      }

      const studentName = detailedFeedbackModal.studentName || "the student";
      setDetailedFeedbackSuccess("Performance feedback successfully sent to student! ✓");
      setDetailedFeedbackModal(null);
      setDetailedFeedbackSuccess("");
      await loadSubmissions(true);
      setActionModal({
        isOpen: true,
        type: "success",
        title: "Feedback Shared Successfully! 🚀",
        message: `Personalized remarks and performance guidance have been delivered to ${studentName} on their My Tutor action center.`,
        confirmText: "Done",
        onConfirm: () => setActionModal((prev) => ({ ...prev, isOpen: false }))
      });
    } catch (err) {
      setDetailedFeedbackError(err.message || "Failed to dispatch feedback");
    } finally {
      setSendingDetailedFeedback(false);
    }
  };

  const toggleSelectStudent = (studentUid) => {
    setSelectedStudentUids((prev) => {
      const next = new Set(prev);
      if (next.has(studentUid)) next.delete(studentUid);
      else next.add(studentUid);
      return next;
    });
  };

  const toggleSelectAllStudents = () => {
    if (selectedStudentUids.size === submissions.length) {
      setSelectedStudentUids(new Set());
    } else {
      setSelectedStudentUids(new Set(submissions.map((s) => s.studentUid)));
    }
  };

  const handleOpenBatchFeedback = (targetUids = null) => {
    const uids = Array.isArray(targetUids) && targetUids.length > 0
      ? targetUids
      : (selectedStudentUids.size > 0 ? Array.from(selectedStudentUids) : submissions.map((s) => s.studentUid));

    const names = uids.map((uid) => {
      const sub = submissions.find((s) => s.studentUid === uid);
      return sub?.name || "Student";
    });

    setBatchFeedbackModal({
      targetUids: uids,
      targetNames: names,
      personalNote: "",
      isAll: uids.length === submissions.length
    });
    setBatchFeedbackError("");
    setBatchFeedbackSuccess("");
  };

  const handleSendBatchFeedback = async (e) => {
    e.preventDefault();
    if (!batchFeedbackModal || !batchFeedbackModal.targetUids.length) return;
    setSendingBatchFeedback(true);
    setBatchFeedbackError("");
    setBatchFeedbackSuccess("");

    try {
      const res = await fetch(`${apiBase}/api/tutor/assignments/${setId}/send-batch-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          studentUids: batchFeedbackModal.targetUids,
          personalNote: batchFeedbackModal.personalNote
        })
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || "Failed to dispatch batch feedback");
      }

      const count = batchFeedbackModal.targetUids.length;
      setBatchFeedbackSuccess(`✓ Teacher feedback successfully dispatched to ${count} student(s)!`);
      setBatchFeedbackModal(null);
      setBatchFeedbackSuccess("");
      setSelectedStudentUids(new Set());
      await loadSubmissions(true);
      setActionModal({
        isOpen: true,
        type: "success",
        title: "Feedback Shared Successfully! 🚀",
        message: `Teacher remarks have been delivered to ${count} student${count === 1 ? "" : "s"} on their My Tutor action center and notified in-app.`,
        confirmText: "Done",
        onConfirm: () => setActionModal((prev) => ({ ...prev, isOpen: false }))
      });
    } catch (err) {
      setBatchFeedbackError(err.message || "Failed to dispatch feedback");
    } finally {
      setSendingBatchFeedback(false);
    }
  };

  const getBandMeta = (band) => {
    switch (band) {
      case "strong":
        return { label: "Mastered / Strong", bg: "#ffffff", color: "#166534", border: "#bbf7d0", dot: "#16a34a", icon: "●" };
      case "good":
        return { label: "Good / Developing", bg: "#ffffff", color: "#1e40af", border: "#bfdbfe", dot: "#2563eb", icon: "●" };
      case "needs_improvement":
        return { label: "Needs Improvement", bg: "#ffffff", color: "#92400e", border: "#fde68a", dot: "#d97706", icon: "●" };
      case "pending":
      case "in_progress":
        return { label: "Pending Evaluation", bg: "#f8fafc", color: "#475569", border: "#e2e8f0", dot: "#94a3b8", icon: "○" };
      case "weak":
      default:
        return { label: "Focus Needed", bg: "#ffffff", color: "#991b1b", border: "#fecaca", dot: "#dc2626", icon: "●" };
    }
  };

  if (loading) {
    return (
      <div className="tutor-assignment-detail" style={{ padding: "32px 24px", maxWidth: "1240px", margin: "0 auto" }}>
        <div className="analysis-box">
          <div className="question-meta">Loading assignment details & cohort data...</div>
        </div>
      </div>
    );
  }

  if (error || !assignment) {
    return (
      <div className="tutor-assignment-detail" style={{ padding: "32px 24px", maxWidth: "1240px", margin: "0 auto" }}>
        <div className="error-text" style={{ padding: "16px", backgroundColor: "#fee2e2", borderRadius: "8px" }}>
          ⚠️ {error || "Assignment not found"}
        </div>
        <button
          type="button"
          className="secondary-button"
          onClick={() => navigate("/tutor/assignments")}
          style={{ marginTop: "16px" }}
        >
          ← Back to Assignments
        </button>
      </div>
    );
  }

  const isTest = assignment.assignmentType === "test";
  const totalAssigned = submissions.length;
  const completedCount = submissions.filter((s) => s.overallStatus === "completed").length;
  const inProgressCount = submissions.filter((s) => s.overallStatus === "in_progress").length;
  const incompleteCount = submissions.filter((s) => s.overallStatus === "incomplete").length;

  const hasPendingHomeworkEvaluations = !isTest && submissions.some((s) =>
    Array.isArray(s.questionSubmissions) &&
    s.questionSubmissions.some((q) => q.hasWork && !q.isEvaluated)
  );
  const totalSlateCount = submissions.reduce((acc, s) => acc + s.slateCount, 0);
  const totalPhotoCount = submissions.reduce((acc, s) => acc + s.photoCount, 0);

  // Active Rubric Calculations
  const activeRubric = rubrics[selectedRubricQIdx] || rubrics[0] || null;
  const activeRubricSum = (activeRubric?.items || []).reduce((acc, it) => acc + (Number(it.points) || 0), 0);
  const isRubricSumValid = activeRubricSum === 100;
  const activeQuestion = questions[selectedRubricQIdx] || questions[0] || null;
  const marksMap = new Map();
  (assignment.questionMarks || []).forEach((qm) => {
    marksMap.set(String(qm.questionId), qm.marks);
  });
  const activeQuestionMarks = activeQuestion ? marksMap.get(String(activeQuestion._id)) || 10 : 10;

  // Sequential Stage Pipeline State & Dependencies
  const hasConfirmedRubrics = isTest
    ? Array.isArray(rubrics) &&
      rubrics.length > 0 &&
      rubrics.every((r) => r.confirmed && (r.items || []).reduce((acc, it) => acc + (Number(it.points) || 0), 0) === 100)
    : true;
  const hasSubmissions = completedCount > 0 || submissions.some((s) => s.overallStatus === "completed" || s.overallStatus === "in_progress");
  const isGraded = assignment.gradingStatus === "ai_graded" || assignment.gradingStatus === "published";
  const isPublished = assignment.gradingStatus === "published";

  const scoredSubmissions = submissions.filter((s) => s.totalMarksAwarded !== null && s.totalMarksAwarded !== undefined);
  const avgScore = scoredSubmissions.length > 0
    ? Math.round(scoredSubmissions.reduce((acc, s) => acc + (s.totalMarksAwarded / (s.totalMarksMax || 1)) * 100, 0) / scoredSubmissions.length)
    : 0;

  const renderCohortDiagnostics = () => {
    if (!cohortDiagnostics || !cohortDiagnostics.overallCohort) return null;
    const overall = cohortDiagnostics.overallCohort;
    const questionWise = cohortDiagnostics.questionWiseCohort || [];
    const selectedQ = questionWise[selectedCohortQIdx] || questionWise[0] || null;

    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          border: "1.5px solid #e2e8f0",
          borderRadius: "14px",
          padding: "20px 22px",
          marginBottom: "24px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
        }}
      >
        {/* Top Header & View Toggle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "20px" }}>📊</span>
              <h2 style={{ fontSize: "17px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                Cohort Performance & Mastery Diagnostics
              </h2>
              <span style={{ fontSize: "11px", fontWeight: "600", padding: "2px 8px", borderRadius: "12px", backgroundColor: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" }}>
                {overall.totalEvaluated} / {overall.totalAssigned} Evaluated
              </span>
            </div>
            <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--muted)" }}>
              Real-time longitudinal mastery parameters across concept, algebra, transformation, and efficiency.
            </p>
          </div>

          {/* View Toggle */}
          <div style={{ display: "inline-flex", backgroundColor: "#f1f5f9", padding: "3px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
            <button
              type="button"
              onClick={() => setCohortDiagView("overall")}
              style={{
                padding: "6px 14px",
                fontSize: "12px",
                fontWeight: "600",
                border: "none",
                borderRadius: "7px",
                cursor: "pointer",
                backgroundColor: cohortDiagView === "overall" ? "#ffffff" : "transparent",
                color: cohortDiagView === "overall" ? "#0f172a" : "#64748b",
                boxShadow: cohortDiagView === "overall" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.15s ease"
              }}
            >
              Overall Cohort View
            </button>
            <button
              type="button"
              onClick={() => setCohortDiagView("questions")}
              style={{
                padding: "6px 14px",
                fontSize: "12px",
                fontWeight: "600",
                border: "none",
                borderRadius: "7px",
                cursor: "pointer",
                backgroundColor: cohortDiagView === "questions" ? "#ffffff" : "transparent",
                color: cohortDiagView === "questions" ? "#0f172a" : "#64748b",
                boxShadow: cohortDiagView === "questions" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.15s ease"
              }}
            >
              Question-wise Breakdown
            </button>
          </div>
        </div>

        {cohortDiagView === "overall" ? (
          <div>
            {/* Key Metric Gauges */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "18px" }}>
              <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Concept Mastery Rate</div>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginTop: "4px" }}>
                  {overall.totalEvaluated > 0 ? `${overall.avgConceptMastery}%` : "—"}
                </div>
                <div style={{ height: "4px", backgroundColor: "#e2e8f0", borderRadius: "2px", marginTop: "6px", overflow: "hidden" }}>
                  <div style={{ width: `${overall.totalEvaluated > 0 ? overall.avgConceptMastery : 0}%`, height: "100%", backgroundColor: "#3b82f6" }} />
                </div>
                <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "4px" }}>
                  {overall.totalEvaluated > 0 ? "Cohort average" : "Awaiting student submissions"}
                </div>
              </div>

              <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Algebraic Accuracy Rate</div>
                <div style={{ fontSize: "20px", fontWeight: "700", color: overall.totalEvaluated > 0 ? (overall.avgAlgebraicAccuracy >= 60 ? "#059669" : "#d97706") : "#0f172a", marginTop: "4px" }}>
                  {overall.totalEvaluated > 0 ? `${overall.avgAlgebraicAccuracy}%` : "—"}
                </div>
                <div style={{ height: "4px", backgroundColor: "#e2e8f0", borderRadius: "2px", marginTop: "6px", overflow: "hidden" }}>
                  <div style={{ width: `${overall.totalEvaluated > 0 ? overall.avgAlgebraicAccuracy : 0}%`, height: "100%", backgroundColor: overall.avgAlgebraicAccuracy >= 60 ? "#10b981" : "#f59e0b" }} />
                </div>
                <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "4px" }}>
                  {overall.totalEvaluated > 0 ? "Calculation & transformation rigor" : "Awaiting student submissions"}
                </div>
              </div>

              <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Avg Step Efficiency Ratio</div>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#6366f1", marginTop: "4px" }}>
                  {overall.totalEvaluated > 0 ? `${overall.avgStepEfficiency}x` : "—"}
                </div>
                <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "4px" }}>
                  {overall.totalEvaluated > 0 ? (overall.avgStepEfficiency <= 1.3 ? "Optimal concise paths" : "Slightly over-expanded") : "Awaiting student submissions"}
                </div>
              </div>
            </div>

            {overall.totalEvaluated === 0 && (
              <div
                style={{
                  padding: "14px 18px",
                  borderRadius: "10px",
                  backgroundColor: "#f8fafc",
                  border: "1px dashed #cbd5e1",
                  color: "#64748b",
                  fontSize: "13px",
                  marginBottom: "18px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px"
                }}
              >
                <span style={{ fontSize: "18px" }}>⏳</span>
                <div>
                  <strong>No student submissions evaluated yet.</strong> Mastery diagnostics across concepts, step efficiency ratios, and algebraic error patterns will automatically populate as soon as students submit their work.
                </div>
              </div>
            )}

            {/* Performance Bands Grid with Student Names */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", textTransform: "uppercase", color: "#475569", marginBottom: "8px" }}>
                Student Performance Distribution (Click student chip to review work):
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "10px" }}>
                {/* Strong */}
                <div style={{ padding: "12px 14px", borderRadius: "8px", backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ color: "#10b981" }}>●</span> Mastered / Strong
                    </span>
                    <span style={{ fontSize: "11px", fontWeight: "700", backgroundColor: "#f1f5f9", padding: "1px 6px", borderRadius: "6px", color: "#334155" }}>
                      {overall.bands?.strong?.length || 0}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {(!overall.bands?.strong || overall.bands.strong.length === 0) ? (
                      <span style={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>None yet</span>
                    ) : (
                      overall.bands.strong.map((st) => (
                        <button
                          key={st.studentUid}
                          type="button"
                          onClick={() => scrollToAndExpandStudent(st.studentUid)}
                          style={{
                            border: "1px solid #cbd5e1",
                            backgroundColor: "#f8fafc",
                            padding: "3px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#0f172a",
                            cursor: "pointer"
                          }}
                          title={`Score: ${st.percentage}% - Click to review`}
                        >
                          {st.name} ({st.percentage}%)
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* Good */}
                <div style={{ padding: "12px 14px", borderRadius: "8px", backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ color: "#2563eb" }}>●</span> Good / Developing
                    </span>
                    <span style={{ fontSize: "11px", fontWeight: "700", backgroundColor: "#f1f5f9", padding: "1px 6px", borderRadius: "6px", color: "#334155" }}>
                      {overall.bands?.good?.length || 0}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {(!overall.bands?.good || overall.bands.good.length === 0) ? (
                      <span style={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>None yet</span>
                    ) : (
                      overall.bands.good.map((st) => (
                        <button
                          key={st.studentUid}
                          type="button"
                          onClick={() => scrollToAndExpandStudent(st.studentUid)}
                          style={{
                            border: "1px solid #cbd5e1",
                            backgroundColor: "#f8fafc",
                            padding: "3px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#0f172a",
                            cursor: "pointer"
                          }}
                          title={`Score: ${st.percentage}% - Click to review`}
                        >
                          {st.name} ({st.percentage}%)
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* Needs Improvement */}
                <div style={{ padding: "12px 14px", borderRadius: "8px", backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ color: "#d97706" }}>●</span> Needs Improvement
                    </span>
                    <span style={{ fontSize: "11px", fontWeight: "700", backgroundColor: "#f1f5f9", padding: "1px 6px", borderRadius: "6px", color: "#334155" }}>
                      {overall.bands?.needs_improvement?.length || 0}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {(!overall.bands?.needs_improvement || overall.bands.needs_improvement.length === 0) ? (
                      <span style={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>None yet</span>
                    ) : (
                      overall.bands.needs_improvement.map((st) => (
                        <button
                          key={st.studentUid}
                          type="button"
                          onClick={() => scrollToAndExpandStudent(st.studentUid)}
                          style={{
                            border: "1px solid #cbd5e1",
                            backgroundColor: "#f8fafc",
                            padding: "3px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#0f172a",
                            cursor: "pointer"
                          }}
                          title={`Score: ${st.percentage}% - Click to review`}
                        >
                          {st.name} ({st.percentage}%)
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* Weak */}
                <div style={{ padding: "12px 14px", borderRadius: "8px", backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ color: "#dc2626" }}>●</span> Focus Needed
                    </span>
                    <span style={{ fontSize: "11px", fontWeight: "700", backgroundColor: "#f1f5f9", padding: "1px 6px", borderRadius: "6px", color: "#334155" }}>
                      {overall.bands?.weak?.length || 0}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {(!overall.bands?.weak || overall.bands.weak.length === 0) ? (
                      <span style={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>None yet</span>
                    ) : (
                      overall.bands.weak.map((st) => (
                        <button
                          key={st.studentUid}
                          type="button"
                          onClick={() => scrollToAndExpandStudent(st.studentUid)}
                          style={{
                            border: "1px solid #cbd5e1",
                            backgroundColor: "#f8fafc",
                            padding: "3px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#0f172a",
                            cursor: "pointer"
                          }}
                          title={`Score: ${st.percentage}% - Click to review`}
                        >
                          {st.name} ({st.percentage}%)
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* Pending */}
                {Array.isArray(overall.bands?.pending) && overall.bands.pending.length > 0 && (
                  <div style={{ padding: "12px 14px", borderRadius: "8px", backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <span style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ color: "#94a3b8" }}>○</span> Pending
                      </span>
                      <span style={{ fontSize: "11px", fontWeight: "700", backgroundColor: "#f1f5f9", padding: "1px 6px", borderRadius: "6px", color: "#64748b" }}>
                        {overall.bands.pending.length}
                      </span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {overall.bands.pending.map((st) => (
                        <button
                          key={st.studentUid}
                          type="button"
                          onClick={() => scrollToAndExpandStudent(st.studentUid)}
                          style={{
                            border: "1px solid #cbd5e1",
                            backgroundColor: "#f8fafc",
                            padding: "3px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#64748b",
                            cursor: "pointer"
                          }}
                          title="Awaiting submission"
                        >
                          {st.name} (Pending)
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Top Pitfalls and Focus Areas */}
            {(overall.topCommonErrors.length > 0 || overall.topFocusPoints.length > 0) && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "12px", marginTop: "12px" }}>
                {overall.topCommonErrors.length > 0 && (
                  <div style={{ padding: "14px 16px", backgroundColor: "#ffffff", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>⚠️</span> Top Cohort Algebraic & Step Pitfalls
                    </div>
                    <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", color: "#334155", lineHeight: "1.6" }}>
                      {overall.topCommonErrors.map((err, i) => (
                        <li key={i} style={{ marginBottom: "3px" }}>
                          {err.error} {err.count > 1 && <span style={{ fontWeight: "700", color: "#0f172a" }}>({err.count} students)</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {overall.topFocusPoints.length > 0 && (
                  <div style={{ padding: "14px 16px", backgroundColor: "#ffffff", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>🎯</span> Recommended Class Focus Actions
                    </div>
                    <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", color: "#334155", lineHeight: "1.6" }}>
                      {overall.topFocusPoints.map((fp, i) => (
                        <li key={i} style={{ marginBottom: "3px" }}>
                          {fp.point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Question-wise Breakdown View */
          <div>
            {/* Question Sub-tabs */}
            <div style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "10px", marginBottom: "14px" }}>
              {questionWise.map((q, idx) => (
                <button
                  key={q.questionId}
                  type="button"
                  onClick={() => setSelectedCohortQIdx(idx)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: "8px",
                    border: selectedCohortQIdx === idx ? "1.5px solid #4f46e5" : "1px solid #cbd5e1",
                    backgroundColor: selectedCohortQIdx === idx ? "#ede9fe" : "#ffffff",
                    color: selectedCohortQIdx === idx ? "#4f46e5" : "#475569",
                    fontWeight: selectedCohortQIdx === idx ? "700" : "500",
                    fontSize: "12px",
                    cursor: "pointer",
                    whiteSpace: "nowrap"
                  }}
                >
                  P{idx + 1}: {q.title} ({q.avgScore} / {q.maxScore} M)
                </button>
              ))}
            </div>

            {selectedQ && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
                  <div>
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
                      {selectedQ.title}
                    </span>
                    <span style={{ marginLeft: "10px", fontSize: "12px", color: "var(--muted)" }}>
                      Cohort Avg: {selectedQ.avgScore} / {selectedQ.maxScore} Marks ({selectedQ.avgPercentage}%)
                    </span>
                  </div>
                </div>

                {/* Question Bands with student chips */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
                  <div style={{ padding: "10px", borderRadius: "8px", backgroundColor: "#ecfdf5", border: "1px solid #a7f3d0" }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#065f46", marginBottom: "6px" }}>
                      🟢 Strong ({selectedQ.bands.strong.length})
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                      {selectedQ.bands.strong.length === 0 ? (
                        <span style={{ fontSize: "11px", color: "#065f46", fontStyle: "italic" }}>None</span>
                      ) : (
                        selectedQ.bands.strong.map((st) => (
                          <button
                            key={st.studentUid}
                            type="button"
                            onClick={() => scrollToAndExpandStudent(st.studentUid)}
                            style={{
                              border: "1px solid #6ee7b7",
                              backgroundColor: "#ffffff",
                              padding: "2px 7px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#065f46",
                              cursor: "pointer"
                            }}
                          >
                            {st.name} ({st.score}M)
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  <div style={{ padding: "10px", borderRadius: "8px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe" }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#1e40af", marginBottom: "6px" }}>
                      🔵 Good ({selectedQ.bands.good.length})
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                      {selectedQ.bands.good.length === 0 ? (
                        <span style={{ fontSize: "11px", color: "#1e40af", fontStyle: "italic" }}>None</span>
                      ) : (
                        selectedQ.bands.good.map((st) => (
                          <button
                            key={st.studentUid}
                            type="button"
                            onClick={() => scrollToAndExpandStudent(st.studentUid)}
                            style={{
                              border: "1px solid #93c5fd",
                              backgroundColor: "#ffffff",
                              padding: "2px 7px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#1e40af",
                              cursor: "pointer"
                            }}
                          >
                            {st.name} ({st.score}M)
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  <div style={{ padding: "10px", borderRadius: "8px", backgroundColor: "#fffbeb", border: "1px solid #fde68a" }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#92400e", marginBottom: "6px" }}>
                      🟡 Needs Improvement ({selectedQ.bands.needs_improvement.length})
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                      {selectedQ.bands.needs_improvement.length === 0 ? (
                        <span style={{ fontSize: "11px", color: "#92400e", fontStyle: "italic" }}>None</span>
                      ) : (
                        selectedQ.bands.needs_improvement.map((st) => (
                          <button
                            key={st.studentUid}
                            type="button"
                            onClick={() => scrollToAndExpandStudent(st.studentUid)}
                            style={{
                              border: "1px solid #fcd34d",
                              backgroundColor: "#ffffff",
                              padding: "2px 7px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#92400e",
                              cursor: "pointer"
                            }}
                          >
                            {st.name} ({st.score}M)
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  <div style={{ padding: "10px", borderRadius: "8px", backgroundColor: "#fef2f2", border: "1px solid #fecaca" }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#991b1b", marginBottom: "6px" }}>
                      🔴 Weak ({selectedQ.bands.weak.length})
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                      {selectedQ.bands.weak.length === 0 ? (
                        <span style={{ fontSize: "11px", color: "#991b1b", fontStyle: "italic" }}>None</span>
                      ) : (
                        selectedQ.bands.weak.map((st) => (
                          <button
                            key={st.studentUid}
                            type="button"
                            onClick={() => scrollToAndExpandStudent(st.studentUid)}
                            style={{
                              border: "1px solid #fca5a5",
                              backgroundColor: "#ffffff",
                              padding: "2px 7px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#991b1b",
                              cursor: "pointer"
                            }}
                          >
                            {st.name} ({st.score}M)
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {selectedQ.topErrors.length > 0 && (
                  <div style={{ marginTop: "12px", padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "4px" }}>
                      Common deductions on this question:
                    </div>
                    <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", color: "#64748b" }}>
                      {selectedQ.topErrors.map((err, i) => (
                        <li key={i}>{err.error} {err.count > 1 && `(${err.count} students)`}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const activeReviewStudent = submissions.find((s) => s.studentUid === reviewStudentUid) || submissions[0] || null;

  const stages = isTest
    ? [
        {
          id: "submissions",
          number: 1,
          title: "Submissions Roster",
          subtitle: `${completedCount}/${totalAssigned} submitted`,
          unlocked: true,
          completed: completedCount > 0
        },
        {
          id: "rubric",
          number: 2,
          title: "Rubric",
          subtitle: isPublished ? "Locked (Published)" : (hasConfirmedRubrics ? "Confirmed" : "Setup Needed"),
          unlocked: true,
          completed: hasConfirmedRubrics
        },
        {
          id: "grading",
          number: 3,
          title: "Grading",
          subtitle: isPublished ? "Locked (Published)" : (isGraded ? "Graded" : (hasConfirmedRubrics && hasSubmissions ? "Ready" : "Locked")),
          unlocked: hasConfirmedRubrics && hasSubmissions,
          completed: isGraded
        },
        {
          id: "review",
          number: 4,
          title: "Review & Overrides",
          subtitle: isGraded ? "Available" : "Locked",
          unlocked: isGraded,
          completed: isGraded
        },
        {
          id: "publish",
          number: 5,
          title: "Publish Grades",
          subtitle: isPublished ? "Published" : (isGraded ? "Ready" : "Locked"),
          unlocked: isGraded,
          completed: isPublished
        }
      ]
    : [
        {
          id: "submissions",
          number: 1,
          title: "Student Roster",
          subtitle: `${completedCount}/${totalAssigned} submitted`,
          unlocked: true,
          completed: completedCount > 0
        },
        {
          id: "cohort",
          number: 2,
          title: "Cohort Analytics",
          subtitle: cohortDiagnostics?.overallCohort?.totalEvaluated > 0 ? `${cohortDiagnostics.overallCohort.totalEvaluated} evaluated` : "Class Insights",
          unlocked: true,
          completed: Boolean(cohortDiagnostics?.overallCohort?.totalEvaluated > 0)
        },
        {
          id: "review",
          number: 3,
          title: "Individual Review",
          subtitle: activeReviewStudent ? activeReviewStudent.name : "Select student",
          unlocked: submissions.length > 0,
          completed: false
        }
      ];

  const renderSubmissionsRoster = () => {
    const filtered = submissions.filter((s) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matchName = (s.name || "").toLowerCase().includes(q);
        const matchEmail = (s.email || "").toLowerCase().includes(q);
        const matchUser = (s.username || "").toLowerCase().includes(q);
        if (!matchName && !matchEmail && !matchUser) return false;
      }
      if (statusFilter === "completed") return s.overallStatus === "completed";
      if (statusFilter === "in_progress") return s.overallStatus === "in_progress";
      if (statusFilter === "incomplete") return s.overallStatus === "incomplete";
      if (statusFilter === "not_started") return s.overallStatus === "not_started";
      return true;
    });

    const notStartedCount = totalAssigned - completedCount - inProgressCount - incompleteCount;

    return (
      <div>
        {/* Problems Preview Collapsible */}
        <details
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid var(--border)",
            borderRadius: "12px",
            padding: "14px 18px",
            marginBottom: "20px"
          }}
        >
          <summary style={{ cursor: "pointer", fontWeight: "600", fontSize: "14px", color: "#0f172a" }}>
            View Assignment Problems ({questions.length})
          </summary>
          <div style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
            {questions.map((q, idx) => {
              const qMarks = marksMap.get(String(q._id)) || 10;
              return (
                <div
                  key={q._id}
                  style={{
                    backgroundColor: "#f8fafc",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0"
                  }}
                >
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "4px" }}>
                    Problem {idx + 1}: {q.title} {isTest && <span style={{ color: "#4f46e5" }}>({qMarks} Marks)</span>}
                  </div>
                  {q.latexRendered && (
                    <div style={{ overflowX: "auto", padding: "2px 0" }}>
                      <BlockMath math={formatLatexForDisplay(q.latexRendered)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </details>

        {/* Controls Bar: Search, Status Filters, Actions */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "16px"
          }}
        >
          {/* Search Box */}
          <div style={{ position: "relative", minWidth: "260px", flex: 1, maxWidth: "360px" }}>
            <input
              type="text"
              placeholder="Search student by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px 8px 32px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                color: "#0f172a",
                backgroundColor: "#ffffff",
                boxSizing: "border-box"
              }}
            />
            <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "13px" }}>🔍</span>
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", border: "none", background: "none", color: "#94a3b8", cursor: "pointer", fontSize: "12px" }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Status Filter Tabs */}
          <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
            {[
              { id: "all", label: `All (${submissions.length})` },
              { id: "completed", label: `Submitted (${completedCount})` },
              { id: "in_progress", label: `In Progress (${inProgressCount})` },
              ...(incompleteCount > 0 ? [{ id: "incomplete", label: `Incomplete (${incompleteCount})` }] : []),
              { id: "not_started", label: `Not Started (${Math.max(0, notStartedCount)})` }
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStatusFilter(tab.id)}
                style={{
                  padding: "5px 11px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: statusFilter === tab.id ? "600" : "500",
                  border: "1px solid",
                  borderColor: statusFilter === tab.id ? "#0f172a" : "#e2e8f0",
                  backgroundColor: statusFilter === tab.id ? "#0f172a" : "#ffffff",
                  color: statusFilter === tab.id ? "#ffffff" : "#475569",
                  cursor: "pointer",
                  transition: "all 0.15s ease"
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Action CTAs */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginLeft: "auto" }}>
            {selectedStudentUids.size > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", backgroundColor: "#f1f5f9", padding: "4px 12px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
                <span style={{ fontSize: "12px", fontWeight: "600", color: "#0f172a" }}>
                  {selectedStudentUids.size} selected
                </span>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => handleOpenBatchFeedback(Array.from(selectedStudentUids))}
                  style={{ fontSize: "12px", padding: "3px 8px" }}
                >
                  ✉ Send Feedback
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedStudentUids(new Set())}
                  style={{ fontSize: "11px", border: "none", background: "none", color: "#64748b", textDecoration: "underline", cursor: "pointer" }}
                >
                  Clear
                </button>
              </div>
            )}

            {!isTest && hasPendingHomeworkEvaluations && (
              <button
                type="button"
                className="primary-button"
                onClick={handleBatchEvaluateHomework}
                disabled={isBatchEvaluating}
                style={{ fontSize: "13px", padding: "7px 14px", backgroundColor: "#059669", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: "6px" }}
              >
                ⚡ {isBatchEvaluating ? "Evaluating..." : "Evaluate All Pending"}
              </button>
            )}

            {submissions.length > 0 && (
              <button
                type="button"
                className="secondary-button"
                onClick={() => handleOpenBatchFeedback(null)}
                style={{ fontSize: "13px", padding: "7px 14px", display: "inline-flex", alignItems: "center", gap: "6px" }}
              >
                ✉ Send Feedback to All ({submissions.length})
              </button>
            )}
          </div>
        </div>

        {/* High-Density Roster Table */}
        {filtered.length === 0 ? (
          <div style={{ backgroundColor: "#ffffff", border: "1px dashed #cbd5e1", borderRadius: "12px", padding: "36px", textAlign: "center", color: "#64748b" }}>
            No students found matching your search / filter.
          </div>
        ) : (
          <div
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              overflowX: "auto",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
            }}
          >
            <table style={{ width: "100%", minWidth: "800px", borderCollapse: "collapse", textAlign: "left", fontSize: "13px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#64748b", fontSize: "12px", textTransform: "uppercase" }}>
                  <th style={{ padding: "12px 16px", width: "36px" }}>
                    <input
                      type="checkbox"
                      checked={selectedStudentUids.size === submissions.length && submissions.length > 0}
                      onChange={toggleSelectAllStudents}
                      style={{ cursor: "pointer" }}
                    />
                  </th>
                  <th style={{ padding: "12px 16px" }}>Student</th>
                  <th style={{ padding: "12px 16px" }}>Status</th>
                  <th style={{ padding: "12px 16px" }}>{isTest ? "Score" : "Progress"}</th>
                  <th style={{ padding: "12px 16px" }}>Mastery Band</th>
                  <th style={{ padding: "12px 16px" }}>Modality</th>
                  <th style={{ padding: "12px 16px", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((sub) => {
                  const isChecked = selectedStudentUids.has(sub.studentUid);
                  const diag = sub.performanceDiagnostics;
                  const isEvaluated = Boolean(diag?.isEvaluated);
                  const bandMeta = getBandMeta(diag?.statusBand || (isEvaluated ? "weak" : "pending"));

                  return (
                    <tr
                      key={sub.studentUid}
                      style={{
                        borderBottom: "1px solid #e2e8f0",
                        backgroundColor: isChecked ? "#f8fafc" : "#ffffff",
                        transition: "background-color 0.1s ease"
                      }}
                    >
                      <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelectStudent(sub.studentUid)}
                          style={{ cursor: "pointer" }}
                        />
                      </td>

                      <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "50%",
                              backgroundColor: "#f1f5f9",
                              color: "#334155",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              fontWeight: "700"
                            }}
                          >
                            {(sub.name || "S").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: "600", color: "#0f172a" }}>{sub.name}</div>
                            {sub.username && (
                              <div style={{ fontSize: "11px", color: "#64748b" }}>@{sub.username}</div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            fontSize: "12px",
                            fontWeight: "500",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            backgroundColor: "#f8fafc",
                            color: "#334155",
                            border: "1px solid #e2e8f0"
                          }}
                        >
                          <span style={{ color: sub.overallStatus === "completed" ? "#10b981" : sub.overallStatus === "incomplete" ? "#f97316" : "#f59e0b", fontSize: "10px" }}>●</span>
                          {sub.overallStatus === "completed" ? "Submitted" : sub.overallStatus === "incomplete" ? "Incomplete" : "In Progress"}
                        </span>
                      </td>

                      <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                        {isTest ? (
                          sub.totalMarksAwarded !== null && sub.totalMarksAwarded !== undefined ? (
                            <div>
                              <span style={{ fontWeight: "700", color: "#0f172a" }}>{sub.totalMarksAwarded}</span>
                              <span style={{ color: "#64748b", fontSize: "11px" }}> / {sub.totalMarksMax} M</span>
                              <span style={{ marginLeft: "6px", fontSize: "11px", color: "#10b981", fontWeight: "600" }}>
                                ({Math.round((sub.totalMarksAwarded / sub.totalMarksMax) * 100)}%)
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: "#64748b", fontSize: "12px" }}>Pending Grading</span>
                          )
                        ) : (
                          <div>
                            <span style={{ fontWeight: "600", color: "#0f172a" }}>{sub.questionsCompleted}</span>
                            <span style={{ color: "#64748b", fontSize: "12px" }}> / {sub.totalQuestions}</span>
                            <span style={{ marginLeft: "6px", fontSize: "11px", fontWeight: "600", color: sub.questionsCompleted === sub.totalQuestions ? "#10b981" : "#d97706" }}>
                              {sub.questionsCompleted === sub.totalQuestions ? "✓ Done" : `${sub.totalQuestions - sub.questionsCompleted} pending`}
                            </span>
                          </div>
                        )}
                      </td>

                      <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            fontSize: "11px",
                            fontWeight: "600",
                            padding: "2px 8px",
                            borderRadius: "6px",
                            backgroundColor: bandMeta.bg,
                            color: bandMeta.color,
                            border: `1px solid ${bandMeta.border}`
                          }}
                        >
                          <span style={{ color: bandMeta.dot }}>●</span>
                          {bandMeta.label} {isEvaluated ? `(${diag?.percentage || 0}%)` : ""}
                        </span>
                      </td>

                      <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                        <span style={{ fontSize: "11px", padding: "2px 6px", borderRadius: "4px", backgroundColor: "#f1f5f9", color: "#475569" }}>
                          {sub.primarySubmissionMode === "photo_upload" ? "Photos" : sub.primarySubmissionMode === "slate" ? "Slate" : sub.primarySubmissionMode === "hybrid" ? "Hybrid" : "Slate"}
                        </span>
                      </td>

                      <td style={{ padding: "12px 16px", verticalAlign: "middle", textAlign: "right" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                          <button
                            type="button"
                            className="primary-button"
                            onClick={() => {
                              setReviewStudentUid(sub.studentUid);
                              setReviewQuestionIdx(0);
                              setCurrentStage("review");
                            }}
                            style={{
                              fontSize: "12px",
                              padding: "4px 10px",
                              backgroundColor: "#0f172a",
                              color: "#ffffff",
                              whiteSpace: "nowrap"
                            }}
                          >
                            Review Work →
                          </button>

                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() => handleOpenFeedback(sub.questionSubmissions[0]?.attemptId, sub.name, questions[0]?.title, sub.questionSubmissions[0]?.tutorFeedback?.notes)}
                            style={{ fontSize: "11px", padding: "4px 8px", whiteSpace: "nowrap" }}
                            title="Add formative teacher remark"
                          >
                            ✉ Remark
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderStudentReviewWorkspace = () => {
    if (submissions.length === 0) {
      return (
        <div style={{ padding: "48px 24px", textAlign: "center", backgroundColor: "#ffffff", borderRadius: "14px", border: "1px dashed #cbd5e1", color: "#64748b" }}>
          No student submissions to review yet.
        </div>
      );
    }

    const activeStudent = submissions.find((s) => s.studentUid === reviewStudentUid) || submissions[0];
    const studentIdx = submissions.findIndex((s) => s.studentUid === activeStudent.studentUid);
    const prevStudent = studentIdx > 0 ? submissions[studentIdx - 1] : null;
    const nextStudent = studentIdx < submissions.length - 1 ? submissions[studentIdx + 1] : null;

    const studentDiag = activeStudent.performanceDiagnostics;
    const isEvaluated = Boolean(studentDiag?.isEvaluated);
    const bandMeta = getBandMeta(studentDiag?.statusBand || (isEvaluated ? "weak" : "pending"));
    const metrics = studentDiag?.metrics || {};
    const qSubmissions = activeStudent.questionSubmissions || [];
    const activeQSub = qSubmissions[reviewQuestionIdx] || qSubmissions[0] || null;
    const activeQ = questions.find((q) => String(q._id) === String(activeQSub?.questionId)) || questions[reviewQuestionIdx] || questions[0] || null;
    const activeQMarks = marksMap.get(String(activeQ?._id)) || 10;

    return (
      <div>
        {/* Top Switcher Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "14px 18px",
            marginBottom: "20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setCurrentStage("submissions")}
              style={{ fontSize: "13px", padding: "6px 12px", display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              ← Back to Student Roster
            </button>
            <span style={{ color: "#cbd5e1" }}>|</span>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <button
                type="button"
                className="secondary-button"
                disabled={!prevStudent}
                onClick={() => {
                  if (prevStudent) {
                    setReviewStudentUid(prevStudent.studentUid);
                    setReviewQuestionIdx(0);
                  }
                }}
                style={{ fontSize: "12px", padding: "5px 10px", opacity: prevStudent ? 1 : 0.4 }}
                title={prevStudent ? `Previous: ${prevStudent.name}` : "First student"}
              >
                ◀ Prev
              </button>

              <select
                value={activeStudent.studentUid}
                onChange={(e) => {
                  setReviewStudentUid(e.target.value);
                  setReviewQuestionIdx(0);
                }}
                style={{
                  padding: "6px 12px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#0f172a",
                  backgroundColor: "#f8fafc",
                  cursor: "pointer"
                }}
              >
                {submissions.map((s, idx) => (
                  <option key={s.studentUid} value={s.studentUid}>
                    {idx + 1}. {s.name} ({s.overallStatus === "completed" ? "Submitted" : s.overallStatus === "incomplete" ? "Incomplete" : "In Progress"})
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="secondary-button"
                disabled={!nextStudent}
                onClick={() => {
                  if (nextStudent) {
                    setReviewStudentUid(nextStudent.studentUid);
                    setReviewQuestionIdx(0);
                  }
                }}
                style={{ fontSize: "12px", padding: "5px 10px", opacity: nextStudent ? 1 : 0.4 }}
                title={nextStudent ? `Next: ${nextStudent.name}` : "Last student"}
              >
                Next ▶
              </button>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                fontSize: "12px",
                fontWeight: "600",
                padding: "3px 10px",
                borderRadius: "20px",
                backgroundColor: "#f1f5f9",
                color: "#334155",
                border: "1px solid #e2e8f0"
              }}
            >
              <span style={{ color: activeStudent.overallStatus === "completed" ? "#10b981" : activeStudent.overallStatus === "incomplete" ? "#f97316" : "#f59e0b", fontSize: "10px" }}>●</span>
              {activeStudent.overallStatus === "completed" ? "Submitted" : activeStudent.overallStatus === "incomplete" ? "Incomplete" : "In Progress"}
            </span>

            <button
              type="button"
              className="secondary-button"
              onClick={() => handleOpenDetailedFeedback(activeStudent)}
              style={{ fontSize: "12px", padding: "6px 14px", backgroundColor: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe", fontWeight: "600" }}
            >
              ✉ Send Feedback to {activeStudent.name.split(" ")[0]}
            </button>
          </div>
        </div>

        {/* Student Mastery & Diagnostics Card (Monochromatic Slate) */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "14px",
            padding: "20px 24px",
            marginBottom: "20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "18px" }}>📊</span>
                <h3 style={{ fontSize: "16px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  {activeStudent.name} — Performance Diagnostics
                </h3>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    backgroundColor: "#f8fafc",
                    color: "#334155",
                    border: "1px solid #e2e8f0"
                  }}
                >
                  <span style={{ color: bandMeta.dot, marginRight: "4px" }}>●</span>
                  {bandMeta.label} ({studentDiag?.percentage || 0}%)
                </span>
              </div>
              <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "3px" }}>
                Measured across conceptual setup, algebraic calculation rigor, transformation depth, and step efficiency.
              </div>
            </div>
          </div>

          {/* 4 Metric Gauges */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" }}>
            <div style={{ padding: "12px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Concept Mastery</div>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginTop: "2px" }}>
                {metrics.conceptScore ?? 0}%
              </div>
              <div style={{ height: "4px", backgroundColor: "#e2e8f0", borderRadius: "2px", marginTop: "6px", overflow: "hidden" }}>
                <div style={{ width: `${metrics.conceptScore ?? 0}%`, height: "100%", backgroundColor: "#3b82f6" }} />
              </div>
            </div>

            <div style={{ padding: "12px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Algebraic Accuracy</div>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginTop: "2px" }}>
                {metrics.algebraicScore ?? 0}%
              </div>
              <div style={{ height: "4px", backgroundColor: "#e2e8f0", borderRadius: "2px", marginTop: "6px", overflow: "hidden" }}>
                <div style={{ width: `${metrics.algebraicScore ?? 0}%`, height: "100%", backgroundColor: (metrics.algebraicScore ?? 0) >= 60 ? "#10b981" : "#f59e0b" }} />
              </div>
            </div>

            <div style={{ padding: "12px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Transformation Fluency</div>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginTop: "2px" }}>
                {metrics.transformationScore ?? 0}%
              </div>
              <div style={{ height: "4px", backgroundColor: "#e2e8f0", borderRadius: "2px", marginTop: "6px", overflow: "hidden" }}>
                <div style={{ width: `${metrics.transformationScore ?? 0}%`, height: "100%", backgroundColor: "#6366f1" }} />
              </div>
            </div>

            <div style={{ padding: "12px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Derivation Steps & Depth</div>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginTop: "2px" }}>
                {metrics.studentStepCount || 0} Steps
              </div>
              <div style={{ fontSize: "11px", color: metrics.isComplete === false ? "#d97706" : "#059669", fontWeight: "600", marginTop: "4px" }}>
                {metrics.isComplete === false
                  ? `Incomplete (${metrics.reachedMilestonesCount || 1} of ${metrics.canonicalStepCount || 3} milestones)`
                  : `${(metrics.stepEfficiencyRatio || 1) <= 1.5 ? "✓ Direct proof" : "✓ Thorough derivation"} (${metrics.canonicalStepCount || 3} milestones)`}
              </div>
            </div>
          </div>

          {/* Strengths & Slips (Clean Slate Cards) */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "12px" }}>
            <div style={{ padding: "12px 16px", backgroundColor: "#ffffff", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                <span>✨</span> Identified Strengths
              </div>
              {Array.isArray(studentDiag?.strengths) && studentDiag.strengths.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", color: "#334155", lineHeight: "1.6" }}>
                  {studentDiag.strengths.map((str, idx) => (
                    <li key={idx}>{str}</li>
                  ))}
                </ul>
              ) : (
                <div style={{ fontSize: "12px", color: "#64748b", fontStyle: "italic" }}>
                  Awaiting detailed step evaluation.
                </div>
              )}
            </div>

            <div style={{ padding: "12px 16px", backgroundColor: "#ffffff", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                <span>⚠️</span> Algebraic Slips & Focus
              </div>
              {Array.isArray(studentDiag?.algebraicErrors) && studentDiag.algebraicErrors.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", color: "#334155", lineHeight: "1.6" }}>
                  {studentDiag.algebraicErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              ) : (
                <div style={{ fontSize: "12px", color: "#64748b", fontStyle: "italic" }}>
                  No significant algebraic slips detected.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Problem Selector Tabs */}
        <div style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "10px", marginBottom: "16px" }}>
          {qSubmissions.map((qSub, qIdx) => {
            const isTabActive = reviewQuestionIdx === qIdx;
            const qMarks = marksMap.get(String(qSub.questionId)) || 10;
            return (
              <button
                key={qSub.questionId || qIdx}
                type="button"
                onClick={() => setReviewQuestionIdx(qIdx)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: isTabActive ? "1.5px solid #0f172a" : "1px solid #cbd5e1",
                  backgroundColor: isTabActive ? "#0f172a" : "#ffffff",
                  color: isTabActive ? "#ffffff" : "#334155",
                  fontWeight: isTabActive ? "700" : "500",
                  fontSize: "13px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.15s ease"
                }}
              >
                <span>Problem {qIdx + 1}: {qSub.title || `Question ${qIdx + 1}`}</span>
                {isTest ? (
                  <span style={{ fontSize: "11px", opacity: 0.85 }}>
                    ({qSub.marksAwarded !== null && qSub.marksAwarded !== undefined ? `${qSub.marksAwarded}/${qSub.marksMax || qMarks} M` : `${qMarks} M`})
                  </span>
                ) : (
                  <span style={{ fontSize: "11px", opacity: 0.85 }}>
                    {qSub.status === "completed" ? "✓ Done" : qSub.status === "incomplete" ? "Draft" : "Pending"}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Active Problem Detailed Workspace */}
        {activeQ && (
          <div
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "14px",
              padding: "24px 28px",
              marginBottom: "24px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
            }}
          >
            {/* Problem Statement Header */}
            <div style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "16px", marginBottom: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "10px" }}>
                <div>
                  <div style={{ fontSize: "12px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>
                    Problem {reviewQuestionIdx + 1}
                  </div>
                  <h3 style={{ fontSize: "17px", fontWeight: "700", color: "#0f172a", margin: "4px 0" }}>
                    {activeQ.title}
                  </h3>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {activeQSub?.submissionType && (
                    <span style={{ fontSize: "12px", padding: "2px 8px", borderRadius: "6px", backgroundColor: "#f1f5f9", color: "#475569", fontWeight: "600" }}>
                      Modality: {activeQSub.submissionType === "photo_upload" ? "Photos" : "Live Slate"}
                    </span>
                  )}
                  {activeQSub?.attemptId && (
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() =>
                        navigate(`/attempts/${activeQSub.attemptId}`, {
                          state: { backTo: location.pathname + location.search }
                        })
                      }
                      style={{ fontSize: "12px", padding: "5px 12px" }}
                    >
                      Open Full Solution Canvas →
                    </button>
                  )}
                </div>
              </div>

              {activeQ.latexRendered && (
                <div style={{ marginTop: "12px", padding: "12px 16px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", overflowX: "auto" }}>
                  <BlockMath math={formatLatexForDisplay(activeQ.latexRendered)} />
                </div>
              )}
            </div>

            {/* Student's Solution Inspection */}
            <div style={{ marginBottom: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <h4 style={{ fontSize: "14px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  Student's Written Solution Steps
                </h4>
                {!isTest && activeQSub?.attemptId && !activeQSub.isEvaluated && activeQSub.hasWork && (
                  <button
                    type="button"
                    className="primary-button"
                    disabled={evaluatingAttemptIds.has(activeQSub.attemptId)}
                    onClick={() => handleEvaluateSingleAttempt(activeQSub.attemptId)}
                    style={{ fontSize: "12px", padding: "4px 10px", backgroundColor: "#059669", color: "#ffffff" }}
                  >
                    {evaluatingAttemptIds.has(activeQSub.attemptId) ? "Evaluating with Erwin..." : "⚡ Evaluate Steps with Erwin AI"}
                  </button>
                )}
              </div>

              {/* Steps List */}
              {Array.isArray(activeQSub?.aiAlignment) && activeQSub.aiAlignment.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {activeQSub.aiAlignment.map((step, sIdx) => {
                    const isCorrect = step.status === "correct";
                    return (
                      <div
                        key={sIdx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 14px",
                          backgroundColor: "#f8fafc",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          gap: "12px"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span
                            style={{
                              width: "22px",
                              height: "22px",
                              borderRadius: "50%",
                              backgroundColor: isCorrect ? "#10b981" : "#f59e0b",
                              color: "#ffffff",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11px",
                              fontWeight: "700"
                            }}
                          >
                            {step.student_step || sIdx + 1}
                          </span>
                          <span style={{ fontFamily: "monospace", fontSize: "13px", color: "#0f172a" }}>
                            {step.student_latex || step.latex || step.step || "Step line"}
                          </span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "600",
                              padding: "2px 8px",
                              borderRadius: "6px",
                              backgroundColor: isCorrect ? "#f0fdf4" : "#fffbeb",
                              color: isCorrect ? "#166534" : "#92400e",
                              border: "1px solid",
                              borderColor: isCorrect ? "#bbf7d0" : "#fde68a"
                            }}
                          >
                            {isCorrect ? "✓ Correct" : "⚠️ Check Step"}
                          </span>
                          {step.matches_canonical_step && (
                            <span style={{ fontSize: "11px", color: "var(--muted)" }}>
                              Milestone {step.matches_canonical_step}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : activeQSub?.hasWork ? (
                <div style={{ padding: "16px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                  Student wrote solution steps on the slate / notebook.
                  <div style={{ marginTop: "8px" }}>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() =>
                        navigate(`/attempts/${activeQSub.attemptId}`, {
                          state: { backTo: location.pathname + location.search }
                        })
                      }
                      style={{ fontSize: "12px" }}
                    >
                      View Handwritten Slate Canvas →
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: "16px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                  No solution submitted for this problem yet.
                </div>
              )}
            </div>

            {/* Test Marks Override Section (If Test) */}
            {isTest && activeQSub?.attemptId && (
              <div style={{ padding: "16px 18px", backgroundColor: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>
                      Marks Awarded: {activeQSub.marksAwarded !== null && activeQSub.marksAwarded !== undefined ? activeQSub.marksAwarded : "—"} / {activeQSub.marksMax || activeQMarks} Marks
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "2px" }}>
                      {activeQSub.hasTutorFeedback ? "Teacher has overridden marks / added remarks" : "Auto-graded by AI Rubric Engine"}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() =>
                      handleOpenOverride(
                        activeQSub.attemptId,
                        activeStudent.name,
                        activeQSub.title || activeQ.title,
                        activeQSub.marksAwarded,
                        activeQSub.marksMax || activeQMarks,
                        activeQSub.tutorFeedback?.notes
                      )
                    }
                    style={{ fontSize: "12px", padding: "6px 12px" }}
                  >
                    ✏ Override Marks / Rubric Notes
                  </button>
                </div>
              </div>
            )}

            {/* Inline Formative Teacher Remarks */}
            {activeQSub?.attemptId && (
              <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "18px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "700", color: "#0f172a", marginBottom: "6px" }}>
                  Teacher's Formative Remark for Problem {reviewQuestionIdx + 1}:
                </label>
                <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "8px" }}>
                  This remark will be sent to {activeStudent.name}'s My Tutor dashboard to guide their next revision steps.
                </div>

                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "10px" }}>
                  {[
                    "+ Sound initial problem setup!",
                    "+ Check sign propagation on step 2.",
                    "+ Valid Vieta formulas, now apply Newton sums for higher powers.",
                    "+ Excellent derivation rigor and concise proof."
                  ].map((sugg, sIdx) => (
                    <button
                      key={sIdx}
                      type="button"
                      onClick={() => {
                        const current = inlineRemarks[activeQSub.attemptId] !== undefined ? inlineRemarks[activeQSub.attemptId] : (activeQSub.tutorFeedback?.notes || "");
                        const separator = current.trim() ? " " : "";
                        setInlineRemarks((prev) => ({
                          ...prev,
                          [activeQSub.attemptId]: current + separator + sugg.replace("+ ", "")
                        }));
                      }}
                      style={{
                        fontSize: "11px",
                        padding: "3px 8px",
                        borderRadius: "6px",
                        backgroundColor: "#f1f5f9",
                        border: "1px solid #cbd5e1",
                        color: "#334155",
                        cursor: "pointer"
                      }}
                    >
                      {sugg}
                    </button>
                  ))}
                </div>

                <textarea
                  value={inlineRemarks[activeQSub.attemptId] !== undefined ? inlineRemarks[activeQSub.attemptId] : (activeQSub.tutorFeedback?.notes || "")}
                  onChange={(e) => {
                    const val = e.target.value;
                    setInlineRemarks((prev) => ({ ...prev, [activeQSub.attemptId]: val }));
                  }}
                  placeholder="Type formative teacher remarks for this problem..."
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                    marginBottom: "10px"
                  }}
                />

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "12px", color: "#059669", fontWeight: "600" }}>
                    {inlineRemarkSaved[activeQSub.attemptId] ? "✓ Remark saved & notified to student!" : ""}
                  </span>

                  <button
                    type="button"
                    className="primary-button"
                    disabled={savingInlineRemarks[activeQSub.attemptId]}
                    onClick={() => handleSaveInlineRemark(activeQSub.attemptId, activeStudent.name, activeQ.title)}
                    style={{ fontSize: "12px", padding: "6px 14px", backgroundColor: "#0f172a", color: "#ffffff" }}
                  >
                    {savingInlineRemarks[activeQSub.attemptId] ? "Saving..." : "Save Remark"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tutor-assignment-detail" style={{ padding: "28px 24px", maxWidth: "1240px", margin: "0 auto" }}>
      {/* ── Top Navigation ── */}
      <button
        type="button"
        className="secondary-button"
        onClick={() => navigate("/tutor/assignments")}
        style={{ marginBottom: "20px", fontSize: "13px" }}
      >
        ← Back to Assignments
      </button>

      {/* ── Assignment Header ── */}
      <div
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid var(--border)",
          borderRadius: "16px",
          padding: "24px 28px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
          {/* Assignment Type Chip */}
          <span
            style={{
              backgroundColor: isTest ? "#fee2e2" : "#eff6ff",
              color: isTest ? "#b91c1c" : "#1d4ed8",
              border: `1px solid ${isTest ? "#fca5a5" : "#bfdbfe"}`,
              fontSize: "12px",
              fontWeight: "700",
              padding: "2px 10px",
              borderRadius: "6px"
            }}
          >
            {isTest ? "Timed Test" : "Homework"}
          </span>

          {/* Classroom Chip */}
          {assignment.classroomName && (
            <span
              style={{
                backgroundColor: "#f0fdf4",
                color: "#166534",
                border: "1px solid #bbf7d0",
                fontSize: "12px",
                fontWeight: "600",
                padding: "2px 10px",
                borderRadius: "6px"
              }}
            >
              Batch: {assignment.classroomName}
            </span>
          )}

          {/* Target Goal Chip */}
          {assignment.targetGoal && (
            <span
              style={{
                backgroundColor: "#ede9fe",
                color: "#6d28d9",
                border: "1px solid #ddd6fe",
                fontSize: "12px",
                fontWeight: "600",
                padding: "2px 10px",
                borderRadius: "6px"
              }}
            >
              {GOAL_LABELS[assignment.targetGoal] || assignment.targetGoal}
            </span>
          )}

          {/* Total Marks Chip (for tests) */}
          {isTest && (
            <span
              style={{
                backgroundColor: "#fef3c7",
                color: "#92400e",
                border: "1px solid #fde68a",
                fontSize: "12px",
                fontWeight: "700",
                padding: "2px 10px",
                borderRadius: "6px"
              }}
            >
              {assignment.totalMarks || questions.length * 10} Total Marks
            </span>
          )}

          {/* Duration Chip (for tests) */}
          {isTest && (
            <span
              style={{
                backgroundColor: "#fffbeb",
                color: "#92400e",
                border: "1px solid #fde68a",
                fontSize: "12px",
                fontWeight: "700",
                padding: "2px 10px",
                borderRadius: "6px"
              }}
            >
              ⏱️ {assignment.durationMinutes || 60}m duration
            </span>
          )}

          {/* Timing Details */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", fontSize: "13px", color: "#475569" }}>
            {isTest && assignment.startTime && (
              <span>
                <strong>Starts:</strong>{" "}
                {new Date(assignment.startTime).toLocaleString("en-IN", {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit"
                })}
              </span>
            )}
            {(assignment.dueTimestamp || assignment.dueDate) && (
              <span>
                <strong>{isTest ? "Deadline:" : "Due:"}</strong>{" "}
                {new Date(assignment.dueTimestamp || assignment.dueDate).toLocaleString("en-IN", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit"
                })}
              </span>
            )}
          </div>
        </div>

        <h1 style={{ fontSize: "24px", fontWeight: "700", margin: "0 0 8px 0", color: "#0f172a" }}>
          {assignment.title}
        </h1>

        {assignment.description && (
          <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "var(--muted)" }}>{assignment.description}</p>
        )}

        {assignment.instructions && (
          <div
            style={{
              backgroundColor: "#f8fafc",
              borderLeft: "3px solid #4f46e5",
              padding: "8px 14px",
              borderRadius: "0 8px 8px 0",
              fontSize: "13px",
              color: "#334155",
              marginBottom: "12px"
            }}
          >
            <strong>Teacher's Instructions:</strong> {assignment.instructions}
          </div>
        )}

        {/* ── Cohort Summary Bar ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: "16px",
            borderTop: "1px solid var(--border)",
            paddingTop: "16px",
            marginTop: "12px"
          }}
        >
          <div>
            <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase" }}>Completion</div>
            <div style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a" }}>
              {completedCount} / {totalAssigned}
            </div>
            <div style={{ fontSize: "11px", color: "var(--muted)" }}>{inProgressCount} in progress</div>
          </div>

          <div>
            <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase" }}>Problems in Set</div>
            <div style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a" }}>{questions.length}</div>
            <div style={{ fontSize: "11px", color: "var(--muted)" }}>
              {isTest ? "100-pt rubric weighted" : "Step equivalence"}
            </div>
          </div>

          <div>
            <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase" }}>Submission Modality</div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "#0f172a", marginTop: "4px" }}>
              {totalSlateCount} Slate • {totalPhotoCount} Photos
            </div>
            <div style={{ fontSize: "11px", color: "var(--muted)" }}>Live vs Notebook</div>
          </div>

          <div>
            <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase" }}>
              {isTest ? "Grading Status" : "Evaluation Mode"}
            </div>
            <div style={{ fontSize: "13px", fontWeight: "700", marginTop: "4px" }}>
              {isTest ? (
                assignment.gradingStatus === "published" ? (
                  <span style={{ color: "#059669" }}>Published</span>
                ) : assignment.gradingStatus === "ai_graded" ? (
                  <span style={{ color: "#7c3aed" }}>AI Graded</span>
                ) : assignment.gradingStatus === "rubric_ready" ? (
                  <span style={{ color: "#2563eb" }}>Rubric Ready</span>
                ) : (
                  <span style={{ color: "#64748b" }}>Draft / Unset</span>
                )
              ) : (
                <span style={{ color: (assignment.gradingStatus === "reviewed" || assignment.isReviewed) ? "#059669" : "#2563eb" }}>
                  {(assignment.gradingStatus === "reviewed" || assignment.isReviewed) ? "✓ Feedback Shared" : "Qualitative Review"}
                </span>
              )}
            </div>
            <div style={{ fontSize: "11px", color: "var(--muted)" }}>
              {isTest ? "Curriculum cohort engine" : "Step diagnostics & remarks"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Sequential Stage Pipeline Bar ── */}
      {stages.length > 0 && (
      <div
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          padding: "8px 10px",
          marginBottom: "24px",
          overflowX: "auto"
        }}
      >
        {stages.map((st) => {
          const isActive = currentStage === st.id;
          return (
            <button
              key={st.id}
              type="button"
              disabled={!st.unlocked}
              onClick={() => setCurrentStage(st.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                borderRadius: "8px",
                border: isActive ? "1px solid #cbd5e1" : "1px solid transparent",
                backgroundColor: isActive ? "#ffffff" : "transparent",
                color: isActive ? "#0f172a" : (st.unlocked ? "#64748b" : "#94a3b8"),
                fontWeight: isActive ? "700" : "500",
                fontSize: "13px",
                cursor: st.unlocked ? "pointer" : "not-allowed",
                whiteSpace: "nowrap",
                boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                transition: "all 0.15s ease",
                opacity: st.unlocked ? 1 : 0.5
              }}
            >
              <span
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  backgroundColor: isActive ? "#0f172a" : (st.completed ? "#10b981" : "#e2e8f0"),
                  color: isActive || st.completed ? "#ffffff" : "#64748b",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "700"
                }}
              >
                {st.completed ? "✓" : st.number}
              </span>
              <span>{st.title}</span>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "500",
                  backgroundColor: isActive ? "#f1f5f9" : "transparent",
                  color: isActive ? "#475569" : "#94a3b8",
                  padding: "1px 6px",
                  borderRadius: "4px"
                }}
              >
                {st.subtitle}
              </span>
            </button>
          );
        })}
      </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STAGE 1: SUBMISSIONS OVERVIEW & ROSTER (ALL MODES)
          ══════════════════════════════════════════════════════════════ */}
      {currentStage === "submissions" && renderSubmissionsRoster()}

      {/* ══════════════════════════════════════════════════════════════
          COHORT ANALYTICS & INSIGHTS (HOMEWORK ONLY)
          ══════════════════════════════════════════════════════════════ */}
      {!isTest && currentStage === "cohort" && (
        <div>
          {renderCohortDiagnostics()}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STAGE 2: 100-PT RUBRIC & GRADING STUDIO (TEST ONLY)
          ══════════════════════════════════════════════════════════════ */}
      {isTest && currentStage === "rubric" && (
        <div>
          {/* Grades Published Lock Warning */}
          {isPublished && (
            <div
              style={{
                padding: "14px 18px",
                borderRadius: "10px",
                marginBottom: "16px",
                fontSize: "13px",
                fontWeight: "600",
                backgroundColor: "#ecfdf5",
                color: "#065f46",
                border: "1px solid #a7f3d0",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}
            >
              <span style={{ fontSize: "16px" }}>🔒</span>
              <div>
                <strong>Grades Finalized & Published:</strong> Rubric criteria are locked in read-only mode to safeguard grading integrity for published student results.
              </div>
            </div>
          )}

          {/* Rubric Explainer Banner */}
          <div
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "16px 20px",
              marginBottom: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "14px"
            }}
          >
            <div>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#0f172a", marginBottom: "4px" }}>
                100-Point Fixed Rubric Engine ({GOAL_LABELS[assignment.targetGoal] || "Curriculum Guidelines"})
              </div>
              <div style={{ fontSize: "13px", color: "var(--muted)", maxWidth: "820px", lineHeight: "1.4" }}>
                Each question's rubric operates on a standardized <strong>100 points total</strong>. The criteria points serve as percentage weights in the final exam score: <em>marks = (points / 100) × maxMarks</em>. The total is non-editable at 100 points to ensure fair and normalized cohort grading.
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                className="primary-button"
                onClick={() => handleGenerateAiRubric()}
                disabled={generatingRubrics || isPublished}
                title={isPublished ? "Rubrics are locked because grades are published" : undefined}
                style={{
                  backgroundColor: isPublished ? "#94a3b8" : "#4f46e5",
                  fontSize: "13px",
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: isPublished ? "not-allowed" : "pointer"
                }}
              >
                {generatingRubrics ? "Generating with AI..." : isPublished ? "Rubrics Locked (Published)" : "Generate AI Rubric for All Questions"}
              </button>
            </div>
          </div>

          {/* Rubric Status / Alerts */}
          {rubricMessage && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                marginBottom: "16px",
                fontSize: "13px",
                fontWeight: "500",
                backgroundColor: rubricMessage.type === "success" ? "#d1fae5" : "#fee2e2",
                color: rubricMessage.type === "success" ? "#065f46" : "#991b1b",
                border: `1px solid ${rubricMessage.type === "success" ? "#a7f3d0" : "#fca5a5"}`
              }}
            >
              {rubricMessage.text}
            </div>
          )}

          {!hasConfirmedRubrics && !rubricMessage && (
            <div
              style={{
                padding: "14px 18px",
                borderRadius: "10px",
                marginBottom: "16px",
                fontSize: "13px",
                lineHeight: "1.5",
                backgroundColor: "#f8fafc",
                color: "#334155",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "12px"
              }}
            >
              <div>
                <span style={{ fontWeight: "700", color: "#0f172a" }}>💡 Default Baseline Template: </span>
                The criteria below are a generic starter template. Click <strong>"Generate AI Rubric for All Questions"</strong> above to produce step-wise criteria calibrated specifically for <strong>{GOAL_LABELS[assignment.targetGoal] || "your selected exam goal"}</strong>.
              </div>
            </div>
          )}

          {/* Question Selector Tabs */}
          <div style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "8px", marginBottom: "16px" }}>
            {questions.map((q, idx) => {
              const qMarks = marksMap.get(String(q._id)) || 10;
              const qRubric = rubrics[idx];
              const qSum = (qRubric?.items || []).reduce((acc, it) => acc + (Number(it.points) || 0), 0);
              const isValid = qSum === 100;

              return (
                <button
                  key={q._id}
                  type="button"
                  onClick={() => setSelectedRubricQIdx(idx)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    border: selectedRubricQIdx === idx ? "2px solid #4f46e5" : "1px solid var(--border)",
                    backgroundColor: selectedRubricQIdx === idx ? "#e0e7ff" : "#ffffff",
                    color: selectedRubricQIdx === idx ? "#3730a3" : "#475569",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}
                >
                  <span>Problem {idx + 1} ({qMarks} Marks)</span>
                  <span
                    style={{
                      fontSize: "10px",
                      padding: "1px 5px",
                      borderRadius: "4px",
                      backgroundColor: isValid ? "#ecfdf5" : "#fee2e2",
                      color: isValid ? "#059669" : "#dc2626"
                    }}
                  >
                    {qSum} pts
                  </span>
                </button>
              );
            })}
          </div>

          {/* Question Rubric Card */}
          {activeRubric && activeQuestion && (
            <div
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid var(--border)",
                borderRadius: "14px",
                padding: "24px",
                marginBottom: "20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
              }}
            >
              {/* Question Summary */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
                <div>
                  <div style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>
                    Problem {selectedRubricQIdx + 1}: {activeQuestion.title}
                  </div>
                  <div style={{ fontSize: "13px", color: "var(--muted)" }}>
                    Exam Weight: <strong>{activeQuestionMarks} Marks</strong> • Goal Context: <strong>{GOAL_LABELS[activeRubric.goal || assignment.targetGoal] || "Standard"}</strong>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => handleGenerateAiRubric(activeQuestion._id)}
                    disabled={generatingRubrics || isPublished}
                    title={isPublished ? "Rubrics are locked because grades are published" : undefined}
                    style={{
                      fontSize: "12px",
                      padding: "6px 12px",
                      cursor: isPublished ? "not-allowed" : "pointer",
                      opacity: isPublished ? 0.6 : 1
                    }}
                  >
                    {generatingRubrics ? "Generating..." : isPublished ? "Locked" : "Regenerate for This Problem"}
                  </button>
                </div>
              </div>

              {/* LaTeX Preview */}
              {activeQuestion.latexRendered && (
                <div style={{ backgroundColor: "#f8fafc", padding: "12px 16px", borderRadius: "8px", marginBottom: "16px", overflowX: "auto" }}>
                  <BlockMath math={formatLatexForDisplay(activeQuestion.latexRendered)} />
                </div>
              )}

              {/* Model / Canonical Solution (if available) */}
              {activeRubric.canonicalSolution && (
                <details style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "10px 14px", marginBottom: "14px" }}>
                  <summary style={{ cursor: "pointer", fontWeight: "600", fontSize: "13px", color: "#166534" }}>
                    Canonical / Model Solution Preview
                  </summary>
                  <div style={{ marginTop: "8px", fontSize: "13px", color: "#14532d", overflowX: "auto" }}>
                    <BlockMath math={activeRubric.canonicalSolution} />
                  </div>
                </details>
              )}

              {/* Discovered Alternative Methods */}
              {Array.isArray(activeRubric.alternativeMethods) && activeRubric.alternativeMethods.length > 0 && (
                <div
                  style={{
                    backgroundColor: "#faf5ff",
                    border: "1px solid #e9d5ff",
                    borderRadius: "8px",
                    padding: "10px 14px",
                    marginBottom: "16px"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: "700", color: "#7e22ce", marginBottom: "6px" }}>
                    <span>💡</span> Valid Alternative Methods Discovered ({activeRubric.alternativeMethods.length})
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {activeRubric.alternativeMethods.map((alt, aIdx) => (
                      <div key={aIdx} style={{ fontSize: "12px", color: "#581c87" }}>
                        <span style={{ fontWeight: "700" }}>• {alt.methodName}</span>: {alt.description || "Alternative mathematically sound approach."}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Criteria Table Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <h3 style={{ fontSize: "15px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  Evaluation Criteria ({activeRubric.items?.length || 0})
                </h3>

                {/* Live Sum Indicator */}
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    padding: "4px 12px",
                    borderRadius: "6px",
                    backgroundColor: isRubricSumValid ? "#ecfdf5" : "#fef2f2",
                    color: isRubricSumValid ? "#059669" : "#dc2626",
                    border: `1.5px solid ${isRubricSumValid ? "#a7f3d0" : "#fca5a5"}`
                  }}
                >
                  {isRubricSumValid ? (
                    <span>✓ Total: 100 / 100 Points</span>
                  ) : (
                    <span>⚠️ Total: {activeRubricSum} / 100 Points (Must equal 100)</span>
                  )}
                </div>
              </div>

              {/* Criteria Rows */}
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "16px" }}>
                {(activeRubric.items || []).map((item, itemIdx) => (
                  <div
                    key={itemIdx}
                    style={{
                      display: "grid",
                      gridTemplateColumns: isPublished ? "1.8fr 2.5fr 85px 2fr" : "1.8fr 2.5fr 85px 2fr 36px",
                      gap: "10px",
                      alignItems: "center",
                      backgroundColor: "#f8fafc",
                      padding: "10px 14px",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    <div>
                      <label style={{ fontSize: "11px", color: "var(--muted)", display: "block", marginBottom: "2px" }}>Criterion</label>
                      <input
                        type="text"
                        value={item.criterion}
                        disabled={isPublished}
                        readOnly={isPublished}
                        onChange={(e) => handleUpdateCriterion(selectedRubricQIdx, itemIdx, "criterion", e.target.value)}
                        placeholder="e.g. Method & Selection"
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          fontSize: "13px",
                          borderRadius: "6px",
                          border: "1px solid var(--border)",
                          boxSizing: "border-box",
                          backgroundColor: isPublished ? "#f1f5f9" : "#ffffff",
                          cursor: isPublished ? "not-allowed" : "text"
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: "11px", color: "var(--muted)", display: "block", marginBottom: "2px" }}>Description / Focus</label>
                      <input
                        type="text"
                        value={item.description}
                        disabled={isPublished}
                        readOnly={isPublished}
                        onChange={(e) => handleUpdateCriterion(selectedRubricQIdx, itemIdx, "description", e.target.value)}
                        placeholder="What evidence must the student show?"
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          fontSize: "13px",
                          borderRadius: "6px",
                          border: "1px solid var(--border)",
                          boxSizing: "border-box",
                          backgroundColor: isPublished ? "#f1f5f9" : "#ffffff",
                          cursor: isPublished ? "not-allowed" : "text"
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: "11px", color: "var(--muted)", display: "block", marginBottom: "2px" }}>Points</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={item.points}
                        disabled={isPublished}
                        readOnly={isPublished}
                        onChange={(e) => handleUpdateCriterion(selectedRubricQIdx, itemIdx, "points", Number(e.target.value) || 0)}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          fontSize: "13px",
                          fontWeight: "700",
                          borderRadius: "6px",
                          border: "1px solid var(--border)",
                          boxSizing: "border-box",
                          backgroundColor: isPublished ? "#f1f5f9" : "#ffffff",
                          cursor: isPublished ? "not-allowed" : "text"
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: "11px", color: "var(--muted)", display: "block", marginBottom: "2px" }}>Deduction Notes</label>
                      <input
                        type="text"
                        value={item.deductionNotes || ""}
                        disabled={isPublished}
                        readOnly={isPublished}
                        onChange={(e) => handleUpdateCriterion(selectedRubricQIdx, itemIdx, "deductionNotes", e.target.value)}
                        placeholder="e.g. -5 for sign error"
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          fontSize: "13px",
                          borderRadius: "6px",
                          border: "1px solid var(--border)",
                          boxSizing: "border-box",
                          backgroundColor: isPublished ? "#f1f5f9" : "#ffffff",
                          cursor: isPublished ? "not-allowed" : "text"
                        }}
                      />
                    </div>

                    {!isPublished && (
                      <div style={{ paddingTop: "14px" }}>
                        <button
                          type="button"
                          onClick={() => handleRemoveCriterion(selectedRubricQIdx, itemIdx)}
                          title="Remove criterion"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#94a3b8",
                            fontSize: "16px"
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Criterion Button */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "10px" }}>
                {!isPublished ? (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => handleAddCriterion(selectedRubricQIdx)}
                    style={{ fontSize: "12px", padding: "6px 12px" }}
                  >
                    + Add Criterion Row
                  </button>
                ) : (
                  <span style={{ fontSize: "12px", color: "var(--muted)", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <span>🔒</span> Rubric criteria locked (Grades Published)
                  </span>
                )}

                <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                  Weight in actual marks: <em>{activeQuestionMarks} Max Marks allocated</em>
                </div>
              </div>
            </div>
          )}

          {/* Confirm & Save Button + Back & Forward CTAs */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginTop: "20px" }}>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setCurrentStage("submissions")}
              style={{ fontSize: "13px", padding: "8px 16px" }}
            >
              ← Back to Submissions
            </button>

            <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                className="primary-button"
                onClick={handleSaveRubrics}
                disabled={savingRubrics || isPublished}
                title={isPublished ? "Rubrics are locked because grades are published" : undefined}
                style={{
                  fontSize: "14px",
                  padding: "10px 24px",
                  backgroundColor: isPublished ? "#94a3b8" : "#059669",
                  cursor: isPublished ? "not-allowed" : "pointer"
                }}
              >
                {savingRubrics ? "Saving 100-Pt Rubrics..." : isPublished ? "Rubrics Locked (Published)" : "Confirm & Save 100-Point Rubrics"}
              </button>

              <button
                type="button"
                className="primary-button"
                onClick={() => setCurrentStage("grading")}
                disabled={!hasConfirmedRubrics}
                style={{
                  fontSize: "14px",
                  padding: "10px 24px",
                  backgroundColor: "#2563eb",
                  opacity: hasConfirmedRubrics ? 1 : 0.5,
                  cursor: hasConfirmedRubrics ? "pointer" : "not-allowed"
                }}
              >
                Proceed to Stage 3: Grading →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Batch Feedback to All / Selected Students ── */}
      {batchFeedbackModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px"
          }}
          onClick={() => !sendingBatchFeedback && setBatchFeedbackModal(null)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              maxWidth: "600px",
              width: "100%",
              padding: "24px 28px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              maxHeight: "90vh",
              overflowY: "auto"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  ✉ Send Feedback to {batchFeedbackModal.isAll ? "All Students" : `${batchFeedbackModal.targetUids.length} Selected Student(s)`}
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--muted)" }}>
                  Dispatches formative remarks and push notifications directly to students.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBatchFeedbackModal(null)}
                disabled={sendingBatchFeedback}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSendBatchFeedback}>
              {batchFeedbackError && (
                <div style={{ padding: "10px 14px", backgroundColor: "#fee2e2", color: "#dc2626", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  ⚠️ {batchFeedbackError}
                </div>
              )}

              {batchFeedbackSuccess && (
                <div style={{ padding: "10px 14px", backgroundColor: "#ecfdf5", color: "#059669", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  {batchFeedbackSuccess}
                </div>
              )}

              {/* Recipient Chips */}
              <div style={{ marginBottom: "16px", padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Recipients ({batchFeedbackModal.targetUids.length}):
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {batchFeedbackModal.targetNames.map((name, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: "12px",
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontWeight: "500"
                      }}
                    >
                      👤 {name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Teacher Remarks Textarea */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                  Teacher Feedback / Remarks:
                </label>
                <textarea
                  value={batchFeedbackModal.personalNote}
                  onChange={(e) => setBatchFeedbackModal({ ...batchFeedbackModal, personalNote: e.target.value })}
                  placeholder="e.g. Great effort on this problem set! Make sure to review the Vieta relationships and higher-degree power sums before tomorrow's session."
                  rows={4}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setBatchFeedbackModal(null)}
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 16px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 18px", backgroundColor: "#4f46e5", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  {sendingBatchFeedback ? "Dispatching..." : `🚀 Send Feedback to ${batchFeedbackModal.targetUids.length} Student(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STAGE 3: COHORT BATCH AI GRADING (TEST ONLY)
          ══════════════════════════════════════════════════════════════ */}
      {isTest && currentStage === "grading" && (
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid var(--border)",
            borderRadius: "14px",
            padding: "28px",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          {/* Grades Published Lock Warning */}
          {isPublished && (
            <div
              style={{
                padding: "14px 18px",
                borderRadius: "10px",
                marginBottom: "20px",
                fontSize: "13px",
                fontWeight: "600",
                backgroundColor: "#ecfdf5",
                color: "#065f46",
                border: "1px solid #a7f3d0",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}
            >
              <span style={{ fontSize: "16px" }}>🔒</span>
              <div>
                <strong>Cohort Grading Finalized:</strong> Grades have been released and published to students. AI Cohort re-grading is locked to preserve published marks. If individual adjustments are needed, use Stage 4: Review to apply score overrides.
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a", marginBottom: "4px" }}>
                Curriculum Cohort Grading Engine
              </div>
              <div style={{ fontSize: "13px", color: "var(--muted)", maxWidth: "700px", lineHeight: "1.5" }}>
                Automated evaluation against confirmed 100-point rubrics using Gemini. All student solutions are analyzed step-by-step with derivation validation and conceptual accuracy scoring.
              </div>
            </div>
            <div>
              {assignment.gradingStatus === "published" && (
                <span style={{ fontSize: "12px", backgroundColor: "#ecfdf5", color: "#059669", fontWeight: "700", padding: "4px 12px", borderRadius: "6px" }}>
                  Published to Students
                </span>
              )}
              {assignment.gradingStatus === "ai_graded" && (
                <span style={{ fontSize: "12px", backgroundColor: "#ede9fe", color: "#6d28d9", fontWeight: "700", padding: "4px 12px", borderRadius: "6px" }}>
                  AI Graded (Ready for Review)
                </span>
              )}
              {assignment.gradingStatus === "rubric_ready" && (
                <span style={{ fontSize: "12px", backgroundColor: "#eff6ff", color: "#1d4ed8", fontWeight: "700", padding: "4px 12px", borderRadius: "6px" }}>
                  Rubric Ready to Grade
                </span>
              )}
              {assignment.gradingStatus === "draft" && (
                <span style={{ fontSize: "12px", backgroundColor: "#f1f5f9", color: "#64748b", fontWeight: "600", padding: "4px 12px", borderRadius: "6px" }}>
                  Draft / Setup Needed
                </span>
              )}
            </div>
          </div>

          {/* Prerequisites / Status Checklist */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "14px",
              marginBottom: "24px",
              backgroundColor: "#f8fafc",
              padding: "16px",
              borderRadius: "10px",
              border: "1px solid #e2e8f0"
            }}
          >
            <div>
              <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>100-Point Rubrics</div>
              <div style={{ fontSize: "14px", fontWeight: "600", color: hasConfirmedRubrics ? "#059669" : "#dc2626" }}>
                {hasConfirmedRubrics ? "✓ All rubrics confirmed (100 pts each)" : "⚠️ Incomplete (check Stage 2)"}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Student Submissions</div>
              <div style={{ fontSize: "14px", fontWeight: "600", color: completedCount > 0 ? "#059669" : "#d97706" }}>
                {completedCount > 0 ? `✓ ${completedCount} submitted attempt(s) ready` : "⚠️ Waiting for student submissions"}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Evaluation State</div>
              <div style={{ fontSize: "14px", fontWeight: "600", color: isGraded ? "#6d28d9" : "#64748b" }}>
                {isGraded ? "✓ Evaluated by AI Engine" : "Ready to execute"}
              </div>
            </div>
          </div>

          {/* Cohort Notification Message */}
          {cohortMessage && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                marginBottom: "20px",
                fontSize: "13px",
                fontWeight: "500",
                backgroundColor: cohortMessage.type === "success" ? "#d1fae5" : "#fee2e2",
                color: cohortMessage.type === "success" ? "#065f46" : "#991b1b",
                border: `1px solid ${cohortMessage.type === "success" ? "#a7f3d0" : "#fca5a5"}`
              }}
            >
              {cohortMessage.text}
            </div>
          )}

          {/* Run Action */}
          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              className="primary-button"
              onClick={handleRunCohortGrading}
              disabled={gradingCohort || !hasConfirmedRubrics || !hasSubmissions || isPublished}
              title={isPublished ? "Cohort grading is locked because grades are published." : undefined}
              style={{
                backgroundColor: isPublished ? "#94a3b8" : "#4f46e5",
                fontSize: "14px",
                padding: "12px 24px",
                cursor: isPublished ? "not-allowed" : "pointer"
              }}
            >
              {gradingCohort
                ? "Grading Cohort with Gemini..."
                : isPublished
                ? "Grading Locked (Grades Published)"
                : isGraded
                ? "Re-run AI Cohort Grading"
                : "Run AI Cohort Grading"}
            </button>
            {!hasSubmissions && (
              <span style={{ fontSize: "13px", color: "#d97706" }}>
                Need at least one submitted student attempt before running cohort grading.
              </span>
            )}
          </div>

          {/* Nav Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "32px",
              borderTop: "1px solid var(--border)",
              paddingTop: "18px"
            }}
          >
            <button
              type="button"
              className="secondary-button"
              onClick={() => setCurrentStage("rubric")}
              style={{ fontSize: "13px", padding: "8px 16px" }}
            >
              ← Back to Rubrics
            </button>

            <button
              type="button"
              className="primary-button"
              onClick={() => setCurrentStage("review")}
              disabled={!isGraded}
              style={{
                fontSize: "13px",
                padding: "10px 20px",
                backgroundColor: "#2563eb",
                opacity: isGraded ? 1 : 0.5,
                cursor: isGraded ? "pointer" : "not-allowed"
              }}
            >
              Proceed to Stage 4: Review Marks →
            </button>
          </div>
        </div>
      )}

      {/* ── Modal: Batch Feedback to All / Selected Students ── */}
      {batchFeedbackModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px"
          }}
          onClick={() => !sendingBatchFeedback && setBatchFeedbackModal(null)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              maxWidth: "600px",
              width: "100%",
              padding: "24px 28px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              maxHeight: "90vh",
              overflowY: "auto"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  ✉ Send Feedback to {batchFeedbackModal.isAll ? "All Students" : `${batchFeedbackModal.targetUids.length} Selected Student(s)`}
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--muted)" }}>
                  Dispatches formative remarks and push notifications directly to students.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBatchFeedbackModal(null)}
                disabled={sendingBatchFeedback}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSendBatchFeedback}>
              {batchFeedbackError && (
                <div style={{ padding: "10px 14px", backgroundColor: "#fee2e2", color: "#dc2626", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  ⚠️ {batchFeedbackError}
                </div>
              )}

              {batchFeedbackSuccess && (
                <div style={{ padding: "10px 14px", backgroundColor: "#ecfdf5", color: "#059669", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  {batchFeedbackSuccess}
                </div>
              )}

              {/* Recipient Chips */}
              <div style={{ marginBottom: "16px", padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Recipients ({batchFeedbackModal.targetUids.length}):
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {batchFeedbackModal.targetNames.map((name, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: "12px",
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontWeight: "500"
                      }}
                    >
                      👤 {name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Teacher Remarks Textarea */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                  Teacher Feedback / Remarks:
                </label>
                <textarea
                  value={batchFeedbackModal.personalNote}
                  onChange={(e) => setBatchFeedbackModal({ ...batchFeedbackModal, personalNote: e.target.value })}
                  placeholder="e.g. Great effort on this problem set! Make sure to review the Vieta relationships and higher-degree power sums before tomorrow's session."
                  rows={4}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setBatchFeedbackModal(null)}
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 16px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 18px", backgroundColor: "#4f46e5", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  {sendingBatchFeedback ? "Dispatching..." : `🚀 Send Feedback to ${batchFeedbackModal.targetUids.length} Student(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STAGE: REVIEW WORKSPACE & OVERRIDES (ALL MODES)
          ══════════════════════════════════════════════════════════════ */}
      {currentStage === "review" && renderStudentReviewWorkspace()}

      {/* ══════════════════════════════════════════════════════════════
          STAGE 5: PUBLISH GRADES & NOTIFY STUDENTS (TEST ONLY)
          ══════════════════════════════════════════════════════════════ */}
      {isTest && currentStage === "publish" && (
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid var(--border)",
            borderRadius: "14px",
            padding: "28px",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a", marginBottom: "6px" }}>
            Release & Publish Cohort Grades
          </div>
          <p style={{ fontSize: "13px", color: "var(--muted)", margin: "0 0 20px 0", lineHeight: "1.5" }}>
            Publishing makes scores, step deductions, and AI-generated diagnostic feedback visible to all assigned students. Once published, students will see their evaluated score on their dashboard.
          </p>

          {/* Stats Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" }}>
            <div style={{ padding: "16px", borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", color: "var(--muted)", textTransform: "uppercase" }}>Total Students</div>
              <div style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", marginTop: "4px" }}>{submissions.length}</div>
            </div>
            <div style={{ padding: "16px", borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", color: "var(--muted)", textTransform: "uppercase" }}>Evaluated Submissions</div>
              <div style={{ fontSize: "24px", fontWeight: "700", color: "#059669", marginTop: "4px" }}>{scoredSubmissions.length}</div>
            </div>
            <div style={{ padding: "16px", borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", color: "var(--muted)", textTransform: "uppercase" }}>Cohort Average</div>
              <div style={{ fontSize: "24px", fontWeight: "700", color: "#2563eb", marginTop: "4px" }}>{avgScore}%</div>
            </div>
            <div style={{ padding: "16px", borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", color: "var(--muted)", textTransform: "uppercase" }}>Publication Status</div>
              <div style={{ fontSize: "14px", fontWeight: "700", marginTop: "8px", color: assignment.gradingStatus === "published" ? "#059669" : "#d97706" }}>
                {assignment.gradingStatus === "published" ? "✓ Published to Students" : "Draft / Unpublished"}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", marginBottom: "24px" }}>
            <button
              type="button"
              className="primary-button"
              onClick={handlePublishGrades}
              disabled={publishingGrades || !isGraded}
              style={{ backgroundColor: "#059669", fontSize: "14px", padding: "12px 24px" }}
            >
              {publishingGrades
                ? "Publishing Grades..."
                : assignment.gradingStatus === "published"
                ? "Re-publish / Sync Updated Marks"
                : "Publish Grades & Feedback to Students"}
            </button>
          </div>

          <div style={{ borderTop: "1px solid var(--border)", paddingTop: "18px" }}>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setCurrentStage("review")}
              style={{ fontSize: "13px", padding: "8px 16px" }}
            >
              ← Back to Review
            </button>
          </div>
        </div>
      )}

      {/* ── Modal: Batch Feedback to All / Selected Students ── */}
      {batchFeedbackModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px"
          }}
          onClick={() => !sendingBatchFeedback && setBatchFeedbackModal(null)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              maxWidth: "600px",
              width: "100%",
              padding: "24px 28px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              maxHeight: "90vh",
              overflowY: "auto"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  ✉ Send Feedback to {batchFeedbackModal.isAll ? "All Students" : `${batchFeedbackModal.targetUids.length} Selected Student(s)`}
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--muted)" }}>
                  Dispatches formative remarks and push notifications directly to students.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBatchFeedbackModal(null)}
                disabled={sendingBatchFeedback}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSendBatchFeedback}>
              {batchFeedbackError && (
                <div style={{ padding: "10px 14px", backgroundColor: "#fee2e2", color: "#dc2626", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  ⚠️ {batchFeedbackError}
                </div>
              )}

              {batchFeedbackSuccess && (
                <div style={{ padding: "10px 14px", backgroundColor: "#ecfdf5", color: "#059669", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  {batchFeedbackSuccess}
                </div>
              )}

              {/* Recipient Chips */}
              <div style={{ marginBottom: "16px", padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Recipients ({batchFeedbackModal.targetUids.length}):
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {batchFeedbackModal.targetNames.map((name, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: "12px",
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontWeight: "500"
                      }}
                    >
                      👤 {name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Teacher Remarks Textarea */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                  Teacher Feedback / Remarks:
                </label>
                <textarea
                  value={batchFeedbackModal.personalNote}
                  onChange={(e) => setBatchFeedbackModal({ ...batchFeedbackModal, personalNote: e.target.value })}
                  placeholder="e.g. Great effort on this problem set! Make sure to review the Vieta relationships and higher-degree power sums before tomorrow's session."
                  rows={4}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setBatchFeedbackModal(null)}
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 16px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 18px", backgroundColor: "#4f46e5", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  {sendingBatchFeedback ? "Dispatching..." : `🚀 Send Feedback to ${batchFeedbackModal.targetUids.length} Student(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Marks Override Modal (For Timed Tests) ── */}
      {overrideModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0 }}>
                Override Marks for {overrideModal.studentName}
              </h2>
              <button
                type="button"
                onClick={() => setOverrideModal(null)}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "var(--muted)" }}
              >
                ✕
              </button>
            </div>

            <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "16px" }}>
              Problem: <strong>{overrideModal.questionTitle}</strong> (Max Marks: {overrideModal.marksMax})
            </div>

            {overrideError && (
              <div className="error-text" style={{ padding: "8px 12px", backgroundColor: "#fee2e2", borderRadius: "8px", marginBottom: "12px", fontSize: "13px" }}>
                {overrideError}
              </div>
            )}

            <form onSubmit={handleSaveOverride}>
              <div className="form-group" style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "4px" }}>
                  Marks Awarded (Out of {overrideModal.marksMax})
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max={overrideModal.marksMax}
                  value={overrideModal.marksAwarded}
                  onChange={(e) =>
                    setOverrideModal((prev) => ({ ...prev, marksAwarded: e.target.value }))
                  }
                  required
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid var(--border)",
                    fontSize: "15px",
                    fontWeight: "700",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "4px" }}>
                  Teacher's Notes / Justification
                </label>
                <textarea
                  rows="3"
                  value={overrideModal.teacherNotes}
                  onChange={(e) =>
                    setOverrideModal((prev) => ({ ...prev, teacherNotes: e.target.value }))
                  }
                  placeholder="e.g. Awarded +2 bonus for rigorous proof in step 3; minor algebraic penalty forgiven."
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid var(--border)",
                    fontSize: "13px",
                    boxSizing: "border-box",
                    fontFamily: "inherit"
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setOverrideModal(null)}
                  disabled={savingOverride}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={savingOverride}
                  style={{ backgroundColor: "#4f46e5" }}
                >
                  {savingOverride ? "Saving..." : "Save Marks Override"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Batch Feedback to All / Selected Students ── */}
      {batchFeedbackModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px"
          }}
          onClick={() => !sendingBatchFeedback && setBatchFeedbackModal(null)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              maxWidth: "600px",
              width: "100%",
              padding: "24px 28px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              maxHeight: "90vh",
              overflowY: "auto"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  ✉ Send Feedback to {batchFeedbackModal.isAll ? "All Students" : `${batchFeedbackModal.targetUids.length} Selected Student(s)`}
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--muted)" }}>
                  Dispatches formative remarks and push notifications directly to students.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBatchFeedbackModal(null)}
                disabled={sendingBatchFeedback}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSendBatchFeedback}>
              {batchFeedbackError && (
                <div style={{ padding: "10px 14px", backgroundColor: "#fee2e2", color: "#dc2626", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  ⚠️ {batchFeedbackError}
                </div>
              )}

              {batchFeedbackSuccess && (
                <div style={{ padding: "10px 14px", backgroundColor: "#ecfdf5", color: "#059669", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  {batchFeedbackSuccess}
                </div>
              )}

              {/* Recipient Chips */}
              <div style={{ marginBottom: "16px", padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Recipients ({batchFeedbackModal.targetUids.length}):
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {batchFeedbackModal.targetNames.map((name, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: "12px",
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontWeight: "500"
                      }}
                    >
                      👤 {name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Teacher Remarks Textarea */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                  Teacher Feedback / Remarks:
                </label>
                <textarea
                  value={batchFeedbackModal.personalNote}
                  onChange={(e) => setBatchFeedbackModal({ ...batchFeedbackModal, personalNote: e.target.value })}
                  placeholder="e.g. Great effort on this problem set! Make sure to review the Vieta relationships and higher-degree power sums before tomorrow's session."
                  rows={4}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setBatchFeedbackModal(null)}
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 16px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 18px", backgroundColor: "#4f46e5", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  {sendingBatchFeedback ? "Dispatching..." : `🚀 Send Feedback to ${batchFeedbackModal.targetUids.length} Student(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Tutor Remark / Feedback Modal (For Homework) ── */}
      {feedbackAttemptId && (
        <div
          className="feedback-modal"
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
          }}
        >
          <div
            className="feedback-card"
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "520px",
              width: "100%",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0 }}>
                Teacher Remark for {feedbackStudentName}
              </h2>
              <button
                type="button"
                onClick={() => setFeedbackAttemptId(null)}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "var(--muted)" }}
              >
                ✕
              </button>
            </div>

            <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "14px" }}>
              Problem: <strong>{feedbackQuestionTitle}</strong>. This remark will appear directly on the student's attempt view.
            </div>

            {feedbackModalError && (
              <div className="error-text" style={{ padding: "8px 12px", backgroundColor: "#fee2e2", borderRadius: "8px", marginBottom: "12px", fontSize: "13px" }}>
                {feedbackModalError}
              </div>
            )}
            {feedbackModalSuccess && (
              <div style={{ padding: "8px 12px", backgroundColor: "#d1fae5", color: "#065f46", borderRadius: "8px", marginBottom: "12px", fontSize: "13px", fontWeight: "500" }}>
                {feedbackModalSuccess}
              </div>
            )}

            <form onSubmit={handleSaveFeedback}>
              <div className="form-group" style={{ marginBottom: "18px" }}>
                <textarea
                  rows="4"
                  value={feedbackNotes}
                  onChange={(e) => setFeedbackNotes(e.target.value)}
                  placeholder="e.g. Great use of trigonometric identities in step 2! Watch out for the negative sign in step 4."
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid var(--border)",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    fontFamily: "inherit"
                  }}
                  autoFocus
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setFeedbackAttemptId(null)}
                  disabled={savingFeedback}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={savingFeedback}
                >
                  {savingFeedback ? "Saving..." : "Save Remark"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Batch Feedback to All / Selected Students ── */}
      {batchFeedbackModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px"
          }}
          onClick={() => !sendingBatchFeedback && setBatchFeedbackModal(null)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              maxWidth: "600px",
              width: "100%",
              padding: "24px 28px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              maxHeight: "90vh",
              overflowY: "auto"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  ✉ Send Feedback to {batchFeedbackModal.isAll ? "All Students" : `${batchFeedbackModal.targetUids.length} Selected Student(s)`}
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--muted)" }}>
                  Dispatches formative remarks and push notifications directly to students.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBatchFeedbackModal(null)}
                disabled={sendingBatchFeedback}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSendBatchFeedback}>
              {batchFeedbackError && (
                <div style={{ padding: "10px 14px", backgroundColor: "#fee2e2", color: "#dc2626", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  ⚠️ {batchFeedbackError}
                </div>
              )}

              {batchFeedbackSuccess && (
                <div style={{ padding: "10px 14px", backgroundColor: "#ecfdf5", color: "#059669", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  {batchFeedbackSuccess}
                </div>
              )}

              {/* Recipient Chips */}
              <div style={{ marginBottom: "16px", padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Recipients ({batchFeedbackModal.targetUids.length}):
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {batchFeedbackModal.targetNames.map((name, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: "12px",
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontWeight: "500"
                      }}
                    >
                      👤 {name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Teacher Remarks Textarea */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                  Teacher Feedback / Remarks:
                </label>
                <textarea
                  value={batchFeedbackModal.personalNote}
                  onChange={(e) => setBatchFeedbackModal({ ...batchFeedbackModal, personalNote: e.target.value })}
                  placeholder="e.g. Great effort on this problem set! Make sure to review the Vieta relationships and higher-degree power sums before tomorrow's session."
                  rows={4}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setBatchFeedbackModal(null)}
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 16px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 18px", backgroundColor: "#4f46e5", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  {sendingBatchFeedback ? "Dispatching..." : `🚀 Send Feedback to ${batchFeedbackModal.targetUids.length} Student(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detailed Student Performance Feedback Modal */}
      {detailedFeedbackModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              maxWidth: "600px",
              width: "100%",
              padding: "24px 28px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              maxHeight: "90vh",
              overflowY: "auto"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  ✉ Dispatch Detailed Performance Feedback
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--muted)" }}>
                  Send structured diagnostic report & encouragement directly to {detailedFeedbackModal.studentName}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDetailedFeedbackModal(null)}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSendDetailedFeedback}>
              {detailedFeedbackError && (
                <div style={{ padding: "10px 14px", backgroundColor: "#fee2e2", color: "#dc2626", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  ⚠️ {detailedFeedbackError}
                </div>
              )}

              {detailedFeedbackSuccess && (
                <div style={{ padding: "10px 14px", backgroundColor: "#ecfdf5", color: "#059669", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  {detailedFeedbackSuccess}
                </div>
              )}

              {/* Pre-populated Strengths */}
              {detailedFeedbackModal.strengths.length > 0 && (
                <div style={{ marginBottom: "14px", padding: "10px 14px", backgroundColor: "#ecfdf5", borderRadius: "8px", border: "1px solid #a7f3d0" }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#065f46", marginBottom: "4px" }}>
                    🌟 Highlighted Strengths:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "12px", color: "#047857" }}>
                    {detailedFeedbackModal.strengths.map((str, i) => (
                      <li key={i}>{str}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Pre-populated Focus Points */}
              {detailedFeedbackModal.focusPoints.length > 0 && (
                <div style={{ marginBottom: "14px", padding: "10px 14px", backgroundColor: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#166534", marginBottom: "4px" }}>
                    🎯 Target Focus Actions:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "12px", color: "#15803d" }}>
                    {detailedFeedbackModal.focusPoints.map((fp, i) => (
                      <li key={i}>{fp}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Teacher Personal Note */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                  Personal Teacher Remark (Included in notification & report):
                </label>
                <textarea
                  value={detailedFeedbackModal.personalNote}
                  onChange={(e) => setDetailedFeedbackModal({ ...detailedFeedbackModal, personalNote: e.target.value })}
                  placeholder="e.g. Great execution on algebraic expansion! Keep a close eye on negative signs when distributing."
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setDetailedFeedbackModal(null)}
                  disabled={sendingDetailedFeedback}
                  style={{ fontSize: "13px", padding: "8px 16px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={sendingDetailedFeedback}
                  style={{ fontSize: "13px", padding: "8px 18px", backgroundColor: "#4f46e5", color: "#ffffff" }}
                >
                  {sendingDetailedFeedback ? "Dispatching..." : "🚀 Send Feedback to Student"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Batch Feedback to All / Selected Students ── */}
      {batchFeedbackModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px"
          }}
          onClick={() => !sendingBatchFeedback && setBatchFeedbackModal(null)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              maxWidth: "600px",
              width: "100%",
              padding: "24px 28px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              maxHeight: "90vh",
              overflowY: "auto"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  ✉ Send Feedback to {batchFeedbackModal.isAll ? "All Students" : `${batchFeedbackModal.targetUids.length} Selected Student(s)`}
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--muted)" }}>
                  Dispatches formative teacher remarks and push notifications directly to students.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBatchFeedbackModal(null)}
                disabled={sendingBatchFeedback}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSendBatchFeedback}>
              {batchFeedbackError && (
                <div style={{ padding: "10px 14px", backgroundColor: "#fee2e2", color: "#dc2626", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  ⚠️ {batchFeedbackError}
                </div>
              )}

              {batchFeedbackSuccess && (
                <div style={{ padding: "10px 14px", backgroundColor: "#ecfdf5", color: "#059669", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  {batchFeedbackSuccess}
                </div>
              )}

              {/* Recipient Chips */}
              <div style={{ marginBottom: "16px", padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Recipients ({batchFeedbackModal.targetUids.length}):
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {batchFeedbackModal.targetNames.map((name, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: "12px",
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontWeight: "500"
                      }}
                    >
                      👤 {name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Teacher Remarks Textarea */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                  Teacher Feedback / Remarks:
                </label>
                <textarea
                  value={batchFeedbackModal.personalNote}
                  onChange={(e) => setBatchFeedbackModal({ ...batchFeedbackModal, personalNote: e.target.value })}
                  placeholder="e.g. Great effort on this problem set! Make sure to review the Vieta relationships and higher-degree power sums before tomorrow's session."
                  rows={4}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setBatchFeedbackModal(null)}
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 16px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 18px", backgroundColor: "#4f46e5", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  {sendingBatchFeedback ? "Dispatching..." : `🚀 Send Feedback to ${batchFeedbackModal.targetUids.length} Student(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Batch Feedback to All / Selected Students ── */}
      {batchFeedbackModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px"
          }}
          onClick={() => !sendingBatchFeedback && setBatchFeedbackModal(null)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              maxWidth: "600px",
              width: "100%",
              padding: "24px 28px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              maxHeight: "90vh",
              overflowY: "auto"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                  ✉ Send Feedback to {batchFeedbackModal.isAll ? "All Students" : `${batchFeedbackModal.targetUids.length} Selected Student(s)`}
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--muted)" }}>
                  Dispatches formative remarks and push notifications directly to students.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBatchFeedbackModal(null)}
                disabled={sendingBatchFeedback}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSendBatchFeedback}>
              {batchFeedbackError && (
                <div style={{ padding: "10px 14px", backgroundColor: "#fee2e2", color: "#dc2626", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  ⚠️ {batchFeedbackError}
                </div>
              )}

              {batchFeedbackSuccess && (
                <div style={{ padding: "10px 14px", backgroundColor: "#ecfdf5", color: "#059669", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>
                  {batchFeedbackSuccess}
                </div>
              )}

              {/* Recipient Chips */}
              <div style={{ marginBottom: "16px", padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Recipients ({batchFeedbackModal.targetUids.length}):
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {batchFeedbackModal.targetNames.map((name, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: "12px",
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontWeight: "500"
                      }}
                    >
                      👤 {name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Teacher Remarks Textarea */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                  Teacher Feedback / Remarks:
                </label>
                <textarea
                  value={batchFeedbackModal.personalNote}
                  onChange={(e) => setBatchFeedbackModal({ ...batchFeedbackModal, personalNote: e.target.value })}
                  placeholder="e.g. Great effort on this problem set! Make sure to review the Vieta relationships and higher-degree power sums before tomorrow's session."
                  rows={4}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setBatchFeedbackModal(null)}
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 16px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={sendingBatchFeedback}
                  style={{ fontSize: "13px", padding: "8px 18px", backgroundColor: "#4f46e5", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  {sendingBatchFeedback ? "Dispatching..." : `🚀 Send Feedback to ${batchFeedbackModal.targetUids.length} Student(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {actionModal.isOpen && (
        <ActionModal
          isOpen={actionModal.isOpen}
          type={actionModal.type}
          title={actionModal.title}
          message={actionModal.message}
          progress={actionModal.progress}
          progressLabel={actionModal.progressLabel}
          detail={actionModal.detail}
          isLocked={actionModal.isLocked}
          confirmText={actionModal.confirmText}
          cancelText={actionModal.cancelText}
          onConfirm={actionModal.onConfirm}
          onCancel={actionModal.onCancel}
        />
      )}
    </div>
  );
}
